import { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Pool, Entry, BracketTeam } from '../types/pool';
import { api } from '../services/api';
import { LineupModal } from '../components/LineupModal';
import { PlayerStats } from '../components/PlayerStats';
import { AdminEditPicksModal } from '../components/AdminEditPicksModal';
import { BracketVisualization } from '../components/BracketVisualization';
import { TournamentBracketView } from '../components/TournamentBracketView';
import { CountryFlag } from '../components/CountryFlag';

// Position order for display
const POSITION_ORDER = [
  'QB',
  'RB1', 'RB2',
  'WR1', 'WR2',
  'TE',
  'FLEX1', 'FLEX2', 'FLEX3', 'FLEX4',
  'K',
  'DEF',
];

// Function to sort positions in the correct order
function sortPositions(positions: [string, any][]): [string, any][] {
  return positions.sort((a, b) => {
    const indexA = POSITION_ORDER.indexOf(a[0]);
    const indexB = POSITION_ORDER.indexOf(b[0]);
    
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    
    return 0;
  });
}

// Helper function to get comprehensive rules content based on pool type
function getPoolRulesContent(poolType: string) {
  const isOlympic = poolType === 'OLYMPIC_HOCKEY';
  const isCFP = poolType === 'CFP';
  const isNFL = poolType === 'NFL_PLAYOFF';
  const isNHL = poolType === 'NHL_PLAYOFF';
  const isNBA = poolType === 'NBA_PLAYOFF';
  const isKnockout = poolType === 'NCAA_BASKETBALL_KNOCKOUT';
  const isPick6 = poolType === 'NCAA_BASKETBALL_PICK6';
  const isF1 = poolType === 'FORMULA_1';
  const isWorldCupPick6 = poolType === 'WORLD_CUP_PICK6';
  const isWorldCupSurvivor = poolType === 'WORLD_CUP_SURVIVOR';
  const isNoRepeat = poolType === 'NFL_NO_REPEAT';

  return {
    overview: isNoRepeat
      ? 'Set a fresh 7-player offense lineup every week of the NFL regular season (Weeks 1–18). The twist: each player can be used only ONCE all season — once he\'s in a submitted lineup, he\'s burned for every remaining week. Weekly scores accumulate; the highest season total after Week 18 wins.'
      : isF1
      ? 'Compete across the entire F1 season! Pick 2 drivers for each of the 24 Grand Prix races. Your drivers earn FIA championship points based on finishing position. Strategic driver usage is key—you can only use each driver a limited number of times!'
      : isOlympic
      ? 'Build a 10-player roster from Olympic hockey players representing different countries. Earn points based on real-world performance throughout the entire Olympic tournament from preliminary rounds through the Gold Medal Game.'
      : isKnockout
      ? 'Survivor-style March Madness pool! Pick teams to win outright each day. If ANY of your picks lose, you\'re eliminated. Last person standing wins the pot!'
      : isPick6
      ? 'Select 6 teams from the tournament bracket. Earn points when your teams win, with higher seeds worth more points. Balance risk and reward!'
      : isWorldCupPick6
      ? 'Pick 6 of the 48 nations at the 2026 FIFA World Cup. Earn points when your teams advance — Seed × Round Factor scoring rewards picking lower-ranked teams that punch above their weight. Higher seeds = more points per round.'
      : isWorldCupSurvivor
      ? 'Two-phase survivor pool for the 2026 FIFA World Cup. Phase 1: pick 6 nations to all advance through the group stage. Phase 2: pick one team per knockout round to win outright. One-and-done across both phases — last entry standing wins the pot.'
      : isNHL
      ? 'Build your 12-player NHL roster and earn fantasy points based on real-world player performance throughout the Stanley Cup Playoffs. Stanley Cup Final points count double!'
      : isNBA
      ? 'Build your 12-player NBA roster and earn fantasy points based on real-world player performance throughout the NBA Playoffs. NBA Finals points count double!'
      : isCFP || isNFL
      ? 'Build your fantasy roster and earn points based on real-world player performance throughout the playoffs. Championship game points count double!'
      : 'Build your roster and compete against other entries. Points accumulate throughout the tournament.',

    rosterFormat: isNoRepeat
      ? {
          total: 7,
          positions: [
            '1 Quarterback (QB)',
            '2 Running Backs (RB)',
            '2 Wide Receivers (WR)',
            '1 Tight End (TE)',
            '1 FLEX (RB, WR, or TE)'
          ],
          constraint: 'ONE USE PER PLAYER, ALL SEASON — once a player appears in any submitted lineup, he is burned for every remaining week (a trade doesn\'t reset him). Offense only: no kickers, no defenses. You submit a NEW 7-man lineup each week.'
        }
      : isF1
      ? null // F1 uses a different format - per-race driver picks
      : isOlympic
      ? {
          total: 10,
          positions: [
            '5 Forwards (F)',
            '4 Defensemen (D)',
            '1 Goaltender (G)'
          ],
          constraint: 'ONE PLAYER PER COUNTRY - You must select players from 10 different countries (12 countries participate, you\'ll use 10)'
        }
      : isCFP
      ? {
          total: 10,
          positions: [
            '1 Quarterback (QB)',
            '2 Running Backs (RB)',
            '2 Wide Receivers (WR)',
            '1 Tight End (TE)',
            '2 FLEX (RB, WR, or TE)',
            '1 Kicker (K)',
            '1 Defense (DEF)'
          ],
          constraint: 'ONE PLAYER PER TEAM - Select players from 10 different CFP teams (12 teams total)'
        }
      : isNFL
      ? {
          total: 12,
          positions: [
            '1 Quarterback (QB)',
            '2 Running Backs (RB)',
            '2 Wide Receivers (WR)',
            '1 Tight End (TE)',
            '4 FLEX (RB, WR, or TE only)',
            '1 Kicker (K)',
            '1 Defense (DEF)'
          ],
          constraint: 'ONE PLAYER PER TEAM - Select players from 12 different playoff teams (14 teams total)'
        }
      : isNHL
      ? {
          total: 12,
          positions: [
            '2 Centers (C)',
            '2 Left Wings (LW)',
            '2 Right Wings (RW)',
            '4 Defensemen (D)',
            '2 Goaltenders (G)'
          ],
          constraint: 'ONE PLAYER PER TEAM - Select players from 12 different playoff teams (16 teams total)'
        }
      : isNBA
      ? {
          total: 12,
          positions: [
            '4 Guards (G)',
            '4 Forwards (F)',
            '2 Centers (C)',
            '2 Flex (any position)'
          ],
          constraint: 'ONE PLAYER PER TEAM - Select players from 12 different playoff teams (16 teams total)'
        }
      : null,

    // Knockout-specific pick requirements
    knockoutPickRequirements: isKnockout
      ? [
          { day: 'Thursday', round: 'Round of 64 (Day 1)', picks: 2, buyBack: 'Yes - $10, must pick 4 on Friday' },
          { day: 'Friday', round: 'Round of 64 (Day 2)', picks: 2, buyBack: 'Yes - $10, must pick 5 on Saturday' },
          { day: 'Saturday', round: 'Round of 32 (Day 1)', picks: 1, buyBack: 'Yes - $10, must pick 6 on Sunday' },
          { day: 'Sunday', round: 'Round of 32 (Day 2)', picks: 1, buyBack: 'No - last day for buy-backs' },
          { day: 'After Sunday', round: 'Sweet 16 onwards', picks: 1, buyBack: 'No' },
        ]
      : null,

    tournamentFormat: isNoRepeat
      ? [
          { phase: 'Weeks 1–18 (Full Regular Season)', description: 'Submit a 7-player lineup each week. Every week counts equally — no playoffs, no multipliers.' },
          { phase: 'Per-Player Locks', description: 'Each slot locks at that player\'s own kickoff. Thursday player locked? Your Sunday and Monday slots are still editable.' },
          { phase: 'Player Pool Management', description: 'With ~7 players burned per week, you\'ll use 120+ players by season\'s end — pace your stars.' },
          { phase: 'Pick-by-Pick Reveal', description: 'Each pick becomes visible to other entrants the moment that player\'s game kicks off — unplayed picks stay hidden.' },
          { phase: '🏆 Season Champion', description: 'Highest cumulative total after Week 18 wins!' }
        ]
      : isF1
      ? [
          { phase: 'Regular Season (Races 1-20)', description: 'Pick 2 drivers per race. Each driver can be used up to 2 times total.' },
          { phase: 'Playoff Cutoff', description: 'After Race 20, top 40% of entries advance to playoffs.' },
          { phase: 'Playoffs (Races 21-24)', description: 'Driver usage resets! Each driver can be used only 1 time.' },
          { phase: 'Sprint Races', description: '6 weekends include sprint races for bonus scoring opportunities.' },
          { phase: '🏆 Season Champion', description: 'Highest total score across all races wins!' }
        ]
      : isOlympic
      ? [
          { phase: 'Preliminary Round', description: 'All 12 teams play 3 round-robin games. All your players earn points.' },
          { phase: 'Quarterfinals', description: '8 teams advance. 4 teams eliminated - their players stop scoring.' },
          { phase: 'Semifinals', description: '4 teams remain. 2 more teams eliminated after this round.' },
          { phase: 'Medal Games', description: 'Bronze Medal Game (3rd place) and Gold Medal Game (1st/2nd place).' },
          { phase: '🥇 Gold Medal Game', description: 'ALL POINTS COUNT DOUBLE! Championship scoring multiplier.' }
        ]
      : isKnockout
      ? [
          { phase: 'Round of 64', description: 'Thursday & Friday - Pick 2 winners each day to survive' },
          { phase: 'Round of 32', description: 'Saturday & Sunday - Pick 1 winner each day' },
          { phase: 'Sweet 16', description: 'Pick 1 winner to advance' },
          { phase: 'Elite 8', description: 'Pick 1 winner to advance' },
          { phase: 'Final Four', description: 'Pick 1 winner - almost there!' },
          { phase: '🏆 Championship', description: 'Pick the champion - last survivor wins!' }
        ]
      : isPick6
      ? [
          { phase: 'Round 1 (64→32)', description: 'Teams earn Seed × 1 points for each win' },
          { phase: 'Round 2 (32→16)', description: 'Teams earn Seed × 3 points for each win' },
          { phase: 'Sweet 16 (16→8)', description: 'Teams earn Seed × 5 points for each win' },
          { phase: 'Elite 8 (8→4)', description: 'Teams earn Seed × 7 points for each win' },
          { phase: 'Final Four', description: 'Teams earn Seed × 9 points for each win' },
          { phase: '🏆 Championship', description: 'Teams earn Seed × 11 points for winning it all!' }
        ]
      : isWorldCupPick6
      ? [
          { phase: 'Group Stage (48→32)', description: 'Top 2 from each group + 8 best 3rd-place teams advance. Teams earn Seed × 4 points for reaching the Round of 32.' },
          { phase: 'Round of 32 (32→16)', description: 'Single elimination. Teams earn Seed × 5 points for each win.' },
          { phase: 'Round of 16 (16→8)', description: 'Teams earn Seed × 7 points for each win.' },
          { phase: 'Quarterfinals (8→4)', description: 'Teams earn Seed × 9 points for each win.' },
          { phase: 'Semifinals (4→2)', description: 'Teams earn Seed × 11 points for each win.' },
          { phase: '🏆 Final', description: 'Teams earn Seed × 13 points for winning it all!' }
        ]
      : isWorldCupSurvivor
      ? [
          { phase: 'Phase 1: Group Stage', description: 'Submit 6 nations before kickoff. ALL 6 must advance to the Round of 32 or you\'re eliminated.' },
          { phase: 'Round of 32 (16 matches)', description: 'Pick 1 team per round to win outright. Used teams cannot be picked again.' },
          { phase: 'Round of 16 (8 matches)', description: 'Pick 1 team to win — extra time and penalties count.' },
          { phase: 'Quarterfinals (4 matches)', description: 'Pick 1 team to win.' },
          { phase: 'Semifinals (2 matches)', description: 'Pick 1 team to win.' },
          { phase: '🏆 Final', description: 'Last entry standing wins the pot.' }
        ]
      : isCFP || isNFL
      ? [
          { phase: 'First Round', description: 'Teams battle in opening playoff games' },
          { phase: 'Divisional/Quarterfinal Round', description: 'Field narrows as teams are eliminated' },
          { phase: 'Conference Championships/Semifinals', description: 'Championship game participants determined' },
          { phase: `🏆 ${isCFP ? 'National Championship' : 'Super Bowl'}`, description: 'ALL POINTS COUNT DOUBLE!' }
        ]
      : isNHL
      ? [
          { phase: 'First Round (Best of 7)', description: '16 teams battle in 8 first-round series' },
          { phase: 'Second Round (Best of 7)', description: '8 remaining teams compete' },
          { phase: 'Conference Finals (Best of 7)', description: 'Final 4 teams fight for a spot in the Final' },
          { phase: '🏆 Stanley Cup Final (Best of 7)', description: 'ALL POINTS COUNT DOUBLE!' }
        ]
      : isNBA
      ? [
          { phase: 'First Round (Best of 7)', description: '16 teams battle in 8 first-round series' },
          { phase: 'Conference Semifinals (Best of 7)', description: '8 remaining teams compete' },
          { phase: 'Conference Finals (Best of 7)', description: 'Final 4 teams fight for a spot in the Finals' },
          { phase: '🏆 NBA Finals (Best of 7)', description: 'ALL POINTS COUNT DOUBLE!' }
        ]
      : null,

    keyRules: isNoRepeat
      ? [
          'Each player can be used exactly ONCE across all 18 weeks — a traded player is still the same player',
          'Each slot locks at that player\'s own kickoff; later-kickoff slots stay editable',
          'Join until the start of Week 1\'s early Sunday games — late joiners can only use players who haven\'t played yet',
          'Empty slots are allowed — they simply score 0 for the week',
          'A player on bye (or inactive) scores 0 but is STILL burned — check schedules before submitting',
          'Miss a week entirely? You score 0 that week, but no players are consumed',
          'Other entrants\' picks reveal player-by-player at each player\'s kickoff — unplayed picks stay hidden',
          'No double-point weeks — the season total decides everything'
        ]
      : isF1
      ? [
          'Pick 2 drivers for each race before the race deadline',
          'Regular Season (Races 1-20): Each driver can be used up to 2 times',
          'Playoffs (Races 21-24): Each driver can only be used 1 time',
          'Driver usage resets when playoffs begin - all drivers available again',
          'Top 40% of entries advance to playoffs - eliminated entries cannot pick',
          'DNF (Did Not Finish) = 0 points unless classified in official results'
        ]
      : isOlympic
      ? [
          'Once a country is eliminated, their players stop earning points (except Bronze Medal Game)',
          'Players only score for games they actually play in',
          'No roster changes after submission - your picks are locked!',
          'All rosters become public after the entry deadline',
          'Check the leaderboard daily to track your standing'
        ]
      : isKnockout
      ? [
          'Each team can only be used ONCE all tournament - choose wisely!',
          'If ANY of your picks lose, you are ELIMINATED',
          'Buy back up to 3 times (Thursday-Saturday only) for $10 each',
          'Buy-backs require extra picks the next day as penalty',
          'Last survivor wins - or survivors split if multiple remain',
          'You must make picks each day before games start'
        ]
      : isPick6
      ? [
          'Select exactly 6 teams from the 68-team bracket',
          'Higher seeds earn more points per win (risk vs reward)',
          'A 12-seed winning Round 1 = 12 pts, a 1-seed = 1 pt',
          'Edit your picks until the tournament starts',
          'All picks become public after first tip-off'
        ]
      : isWorldCupPick6
      ? [
          'Select exactly 6 of the 48 World Cup nations',
          'Higher FIFA-rank seeds (9–12) score more points per round than top seeds',
          'Group-stage advancement awards Seed × 4 points (paid once when team reaches R32)',
          'Knockout round factors: ×5, ×7, ×9, ×11, ×13 — points compound as teams advance',
          'A Seed 12 winning the tournament = 588 points; a Seed 1 winning = only 49',
          'Edit your picks freely until kickoff of the first match (June 11, 2026)',
          'All picks become public after the tournament begins'
        ]
      : isWorldCupSurvivor
      ? [
          'Two phases: 6 group-stage picks before kickoff, then 1 pick per knockout round',
          'Phase 1: ALL 6 of your group-stage picks must advance to R32 or you\'re eliminated',
          'Phase 2: your knockout pick must win outright (extra time / penalties count)',
          'One-and-done: a team used in Phase 1 OR an earlier knockout round cannot be re-used',
          'Phase 1 teams are permanently consumed whether they advance or not',
          'If you have no unused team available for a round, you\'re auto-eliminated',
          'Each round\'s deadline = first match of that round; picks lock when it kicks off',
          'Last entry standing wins the full pot'
        ]
      : [
          'Once a team is eliminated from the playoffs, their players stop earning points',
          'Players only score for games they actually play in',
          'No roster changes after submission - your picks are locked!',
          'All rosters become public after the entry deadline',
          `${isNHL ? 'Stanley Cup Final' : isNBA ? 'NBA Finals' : 'Championship game'} points count DOUBLE`
        ],

    strategyTips: isNoRepeat
      ? [
          { tip: 'Don\'t burn your stars early', reason: 'You only get each player once — save elite players for their best matchups, not just Week 1' },
          { tip: 'Plan around bye weeks', reason: 'A burned player who\'s on bye scores 0 — check the schedule before locking in a lineup' },
          { tip: 'Depth beats stars over 18 weeks', reason: 'You\'ll use 120+ players by season\'s end — knowing the league\'s second tier wins this format' },
          { tip: 'Use late kickoffs as insurance', reason: 'Slots for Sunday/Monday players stay editable after early games — react to inactives and news' }
        ]
      : isF1
      ? [
          { tip: 'Spread top drivers across races', reason: 'Using Max Verstappen in Race 1 means you can only use him once more before playoffs' },
          { tip: 'Save reliable drivers for playoffs', reason: 'With only 1 use per driver in playoffs, you need your best picks for Races 21-24' },
          { tip: 'Consider track history', reason: 'Some drivers excel at specific circuits - match drivers to their strongest tracks' },
          { tip: 'Watch for sprint weekends', reason: '6 weekends have sprint races for extra scoring - plan driver usage accordingly' }
        ]
      : isOlympic
      ? [
          { tip: 'Pick players from medal favorites', reason: 'Teams like Canada, USA, Sweden, Finland typically advance deep, giving their players more games to score' },
          { tip: 'Goaltenders are high-risk, high-reward', reason: 'A starting goalie from a top team can dominate scoring with wins, saves, and shutouts' },
          { tip: 'Balance star power with team strength', reason: 'The best player on a weak team may get eliminated early and have fewer scoring opportunities' },
          { tip: 'Monitor roster announcements', reason: 'National team rosters are announced closer to the Olympics - check for NHL player participation' }
        ]
      : isKnockout
      ? [
          { tip: 'Save top seeds for later rounds', reason: '1-seeds almost never lose early - save them for Sweet 16+ when options are limited' },
          { tip: 'Use mid-seeds early', reason: '7-10 seeds often win Round 1 but lose Round 2 - use them Thursday/Friday' },
          { tip: 'Avoid early Cinderellas', reason: '12-15 seeds can upset but rarely survive multiple rounds' },
          { tip: 'Track your used teams carefully', reason: 'Running out of viable teams = automatic elimination' }
        ]
      : isPick6
      ? [
          { tip: 'Mix high and low seeds', reason: 'High seeds (12-16) offer big points but low win probability' },
          { tip: 'Target Sweet 16 teams', reason: 'Points multiply as rounds progress - teams that go deep are most valuable' },
          { tip: 'Consider upset potential', reason: 'A 12-seed reaching Elite 8 = 12×1 + 12×3 + 12×5 + 12×7 = 192 points!' },
          { tip: 'Watch conference tournaments', reason: 'Hot teams entering March often continue their momentum' }
        ]
      : isWorldCupPick6
      ? [
          { tip: 'Mix tiers — don\'t go all-chalk or all-Cinderella', reason: 'A balanced lineup of two top seeds plus four mid/long-shots tends to outperform either extreme' },
          { tip: 'Hunt for Seed 9–12 teams in weak groups', reason: 'A long shot just clearing the group stage already pays 36–48 points; a 1-seed needs to win the whole thing for 49' },
          { tip: 'Avoid stacking one group', reason: 'Only 3 of 4 teams advance from each group, so two of your picks in the same group risks eating each other' },
          { tip: 'Discount Seed 1–4 only slightly', reason: 'They\'re the most likely to reach SF/Final where ×11 and ×13 multipliers compound — not high-EV but high-floor' }
        ]
      : isWorldCupSurvivor
      ? [
          { tip: 'Burn weak teams in Phase 1', reason: 'You need 6 group-stage advancers — pack the safest picks here so your top tier survives for knockout rounds' },
          { tip: 'Save Seed 1–3 for SF and Final', reason: 'One-and-done means you only get 5 knockout picks; spend them where the matchup math is hardest' },
          { tip: 'Don\'t waste a top tier in R32', reason: 'A well-timed Seed 4–6 against a weak R32 opponent is a better use of inventory than burning Argentina or France' },
          { tip: 'Track inventory like a chess opening', reason: 'If you have no unused team for a round, you\'re auto-eliminated — count remaining options before every knockout pick' }
        ]
      : [
          { tip: 'Prioritize teams expected to advance', reason: 'Players on deep playoff runs accumulate more points over more games' },
          { tip: 'Don\'t stack one team', reason: 'The one-player-per-team rule forces roster diversity' },
          { tip: `${isNHL ? 'Stanley Cup Final' : isNBA ? 'NBA Finals' : 'Championship game'} matters most`, reason: 'Double points can swing the leaderboard dramatically' },
          { tip: 'Check injury reports', reason: 'Injured players may play limited minutes or miss games entirely' }
        ]
  };
}

/** Payment method display labels (Payment Tracking v2). */
const PAYMENT_METHOD_LABELS: Record<string, string> = {
  PAYPAL: 'PayPal',
  CASHAPP: 'Cash App',
  VENMO: 'Venmo',
  CASH: 'Cash',
  COMP: 'Comped',
  OTHER: 'Other',
};

/**
 * Buy-back button for eliminated knockout entries (Payment Tracking v2).
 * No payment form — the $10 fee goes onto the user's balance and they
 * settle up afterwards.
 */
function BuyBackButton({ poolId, entryId, onSuccess }: { poolId: string; entryId: string; onSuccess: () => void }) {
  const { getAccessTokenSilently } = useAuth0();
  const [showConfirm, setShowConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [buyBackError, setBuyBackError] = useState<string | null>(null);

  const handleBuyBack = async () => {
    try {
      setProcessing(true);
      setBuyBackError(null);
      const token = await getAccessTokenSilently();
      await api.requestBuyBack(poolId, entryId, token);
      setShowConfirm(false);
      onSuccess();
    } catch (err: any) {
      setBuyBackError(err instanceof Error ? err.message : 'Failed to process buy-back');
    } finally {
      setProcessing(false);
    }
  };

  if (!showConfirm) {
    return (
      <button
        onClick={() => setShowConfirm(true)}
        className="mt-3 w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
      >
        Buy Back In — $10
      </button>
    );
  }

  return (
    <div className="mt-3 bg-white border border-gray-200 rounded-lg p-4">
      <h4 className="font-semibold text-gray-900 mb-2">Buy Back — $10</h4>
      <p className="text-sm text-gray-600 mb-3">
        Your entry goes right back into the tournament. The $10 fee is added to
        your balance — pay it from the Settle Up page whenever works for you.
      </p>

      {buyBackError && (
        <p className="text-sm text-red-600 mb-3">{buyBackError}</p>
      )}
      <div className="flex gap-2">
        <button
          onClick={handleBuyBack}
          disabled={processing}
          className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm"
        >
          {processing ? 'Processing...' : 'Confirm Buy-Back ($10)'}
        </button>
        <button
          onClick={() => { setShowConfirm(false); setBuyBackError(null); }}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export function PoolDetail() {
  const { poolId } = useParams<{ poolId: string }>();
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();

  const [pool, setPool] = useState<Pool | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [myEntries, setMyEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null); // track which entry ID is being deleted
  const [showMyPicks, setShowMyPicks] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<Entry | null>(null);
  const [playerStats, setPlayerStats] = useState<any[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [editingEntry, setEditingEntry] = useState<Entry | null>(null);
  const [completing, setCompleting] = useState(false);
  const [bracketTeams, setBracketTeams] = useState<BracketTeam[]>([]);
  const [_bracketLoading, _setBracketLoading] = useState(false);
  const [showBracket, setShowBracket] = useState(false);
  const [eliminatedTeamIds, setEliminatedTeamIds] = useState<Set<string>>(new Set());
  // Master World Cup events (with home/away teams and scheduledAt). Used by the
  // WC Survivor leaderboard to derive the "current knockout round" and to label
  // the per-round pick column with a kickoff-time tooltip.
  const [wcMasterEvents, setWcMasterEvents] = useState<any[]>([]);

  // The "active" WC knockout round for the leaderboard pick column.
  // Definition: the earliest knockout round (R32 → R16 → QF → SF → Final) that
  // still has at least one game not finalized (no winnerId). If every knockout
  // game is final, fall back to "Final" so the leaderboard always shows a
  // sensible column. Returns null if no knockout events exist yet (group stage).
  // Also returns the round's first kickoff so we can show "Hidden until 6:00 PM"
  // tooltips on entries whose pick the backend has redacted.
  const wcActiveKnockoutRound = useMemo<{
    label: string;
    backendKey: string;
    firstKickoff: Date | null;
  } | null>(() => {
    if (wcMasterEvents.length === 0) return null;
    const KNOCKOUT_ROUNDS: { label: string; aliases: string[]; backendKey: string }[] = [
      { label: 'Round of 32',  aliases: ['Round of 32'],                                       backendKey: 'R32' },
      { label: 'Round of 16',  aliases: ['Round of 16'],                                       backendKey: 'R16' },
      { label: 'Quarterfinals', aliases: ['Quarter-Finals', 'Quarterfinals', 'Quarter Finals'], backendKey: 'QF' },
      { label: 'Semifinals',   aliases: ['Semi-Finals', 'Semifinals', 'Semi Finals'],          backendKey: 'SF' },
      { label: 'Final',        aliases: ['Final'],                                              backendKey: 'FINAL' },
    ];
    let lastWithEvents: typeof KNOCKOUT_ROUNDS[number] | null = null;
    for (const r of KNOCKOUT_ROUNDS) {
      const matchEvents = wcMasterEvents.filter((e) => r.aliases.includes(e.round));
      if (matchEvents.length === 0) continue;
      lastWithEvents = r;
      const allFinal = matchEvents.every((e) => !!e.winnerId);
      if (!allFinal) {
        const firstKickoff = matchEvents
          .map((e) => new Date(e.scheduledAt))
          .filter((d) => !isNaN(d.getTime()))
          .sort((a, b) => a.getTime() - b.getTime())[0] || null;
        return { label: r.label, backendKey: r.backendKey, firstKickoff };
      }
    }
    if (lastWithEvents) {
      const matchEvents = wcMasterEvents.filter((e) => lastWithEvents!.aliases.includes(e.round));
      const firstKickoff = matchEvents
        .map((e) => new Date(e.scheduledAt))
        .filter((d) => !isNaN(d.getTime()))
        .sort((a, b) => a.getTime() - b.getTime())[0] || null;
      return { label: lastWithEvents.label, backendKey: lastWithEvents.backendKey, firstKickoff };
    }
    return null;
  }, [wcMasterEvents]);

  // Track if we've already loaded data to prevent duplicate calls
  const hasLoadedPool = useRef<string | null>(null);
  const hasLoadedMyEntry = useRef<string | null>(null);

  // Load pool data (public, no auth needed)
  useEffect(() => {
    if (poolId && hasLoadedPool.current !== poolId) {
      hasLoadedPool.current = poolId;
      hasLoadedMyEntry.current = null; // Reset my entry loading for new pool
      loadPool();
    }
  }, [poolId]);

  // Load user-specific data after auth is ready and pool is loaded
  useEffect(() => {
    if (!authLoading && isAuthenticated && pool && hasLoadedMyEntry.current !== pool.id) {
      hasLoadedMyEntry.current = pool.id;
      loadCurrentUser();
      loadMyEntries();
    }
  }, [authLoading, isAuthenticated, pool]);

  const loadPool = async () => {
    try {
      setLoading(true);
      setError(null);

      // First load pool (supports both slug and ID)
      const poolData = await api.getPool(poolId!);
      setPool(poolData);

      // Use actual pool ID for subsequent API calls
      const actualPoolId = poolData.id;

      // Load entries using actual pool ID
      const entriesData = await api.getPoolEntries(actualPoolId);
      setEntries(entriesData);

      // Load player stats only if pool is not OPEN (after it locks)
      if (poolData.status !== 'OPEN') {
        loadPlayerStats(actualPoolId);
      }

      // Load bracket data and eliminated teams for NCAA Basketball pools
      if (['NCAA_BASKETBALL_PICK6', 'NCAA_BASKETBALL_KNOCKOUT'].includes(poolData.type)) {
        loadBracket(actualPoolId);
        api.getEliminatedNcaaTeams()
          .then(data => setEliminatedTeamIds(new Set(data.eliminatedTeamIds || [])))
          .catch(() => {});
      }

      // Load master WC events for the leaderboard's current-round column.
      // Fetched for both WC pool types since the leaderboard layout (per-round
      // pick reveal) is consistent across them.
      if (['WORLD_CUP_PICK6', 'WORLD_CUP_SURVIVOR'].includes(poolData.type)) {
        api.getWorldCupMasterEvents()
          .then((res) => setWcMasterEvents(res.events || []))
          .catch(() => {});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pool');
      console.error('Error loading pool:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMyEntries = async () => {
    if (!pool) return;
    try {
      const token = await getAccessTokenSilently();
      const myEntriesData = await api.getMyEntries(pool.id, token);
      setMyEntries(myEntriesData);
    } catch (err) {
      // Silently handle - no entries found
      setMyEntries([]);
    }
  };

  const loadCurrentUser = async () => {
    try {
      const token = await getAccessTokenSilently();
      const user = await api.getCurrentUser(token);
      setCurrentUser(user);
    } catch (err) {
      console.error('Failed to load current user:', err);
    }
  };

  const loadPlayerStats = async (actualPoolId: string) => {
    try {
      setStatsLoading(true);
      const stats = await api.getPlayerStats(actualPoolId);
      setPlayerStats(stats);
    } catch (err) {
      console.error('Error loading player stats:', err);
      // Don't fail the whole page if stats fail to load
      setPlayerStats([]);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadBracket = async (actualPoolId: string) => {
    try {
      _setBracketLoading(true);
      const bracketData = await api.getPoolBracket(actualPoolId);

      // Load eliminated teams from the cross-pool endpoint
      let eliminatedIds = new Set<string>();
      try {
        const eliminatedData = await api.getEliminatedNcaaTeams();
        eliminatedIds = new Set(eliminatedData.eliminatedTeamIds || []);
      } catch {
        // No matchup data available — that's fine
      }

      // Convert to BracketTeam format with eliminated status
      const teams: BracketTeam[] = bracketData.teams.map(t => ({
        id: t.id,
        name: t.name,
        seed: t.seed,
        region: t.region as any || 'East',
        conference: t.conference,
        eliminated: eliminatedIds.has(t.id),
      }));
      setBracketTeams(teams);
    } catch (err) {
      console.error('Error loading bracket:', err);
      setBracketTeams([]);
    } finally {
      _setBracketLoading(false);
    }
  };

  const handleDeleteEntry = async (entryToDelete: Entry) => {
    if (!pool || !window.confirm(`Are you sure you want to delete "${entryToDelete.entryName || 'this entry'}"? This cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(entryToDelete.id);
      const token = await getAccessTokenSilently();
      await api.deleteEntry(pool.id, entryToDelete.id, token);

      alert('Entry deleted successfully');
      setMyEntries(prev => prev.filter(e => e.id !== entryToDelete.id));
      hasLoadedMyEntry.current = null; // Allow re-checking for entries
      // Reload entries to update leaderboard
      const entriesData = await api.getPoolEntries(pool.id);
      setEntries(entriesData);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete entry');
      console.error('Error deleting entry:', err);
    } finally {
      setDeleting(null);
    }
  };

  const handleCompletePool = async () => {
    if (!pool || !window.confirm('Are you sure you want to mark this pool as complete? This will finalize all prize distributions and cannot be undone.')) {
      return;
    }

    try {
      setCompleting(true);
      const token = await getAccessTokenSilently();
      await api.completePool(pool.id, token);

      alert('Pool marked as complete! Prize distributions are now finalized.');
      hasLoadedPool.current = null; // Allow reload
      loadPool(); // Reload to show completed status and prizes
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to mark pool as complete');
      console.error('Error completing pool:', err);
    } finally {
      setCompleting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      OPEN: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
      LOCKED: 'bg-amber-100 text-amber-700 border border-amber-200',
      IN_PROGRESS: 'bg-blue-100 text-blue-700 border border-blue-200',
      COMPLETED: 'bg-gray-100 text-gray-700 border border-gray-200',
      CANCELLED: 'bg-red-100 text-red-700 border border-red-200',
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-700 border border-gray-200';
  };

  const formatDeadline = (deadline: string) => {
    const date = new Date(deadline);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    
    if (diff < 0) {
      return 'Deadline passed';
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      return `${days}d ${hours}h remaining`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else {
      return `${minutes}m remaining`;
    }
  };

  const formatPicks = (picks: any) => {
    if (!picks) return null;

    // ROSTER_SELECTION (CFP, NFL, NHL, NBA, Olympic Hockey, Four Nations)
    if (picks.type === 'ROSTER_SELECTION' && picks.positions) {
      return sortPositions(Object.entries(picks.positions)).map(([position, pick]: [string, any]) => (
        <div key={position} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0">
          <span className="font-medium text-gray-700">{position}:</span>
          <span className="text-gray-900">{pick.playerName}</span>
        </div>
      ));
    }

    // DAILY_PICKS (Knockout)
    if (picks.type === 'DAILY_PICKS' && picks.picks) {
      const days = Object.entries(picks.picks as Record<string, any[]>).sort(([a], [b]) => a.localeCompare(b));
      if (days.length === 0) return <p className="text-gray-500 text-sm">No picks yet</p>;
      return (
        <div className="space-y-3">
          {days.map(([date, teamPicks]) => (
            <div key={date}>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </p>
              {(teamPicks as any[]).map((tp: any) => (
                <div key={tp.teamId} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0">
                  <span className="text-gray-900">
                    <span className="font-bold text-gray-500">#{tp.seed}</span> {tp.teamName}
                  </span>
                  {tp.result && tp.result !== 'PENDING' && (
                    <span className={tp.result === 'WIN' ? 'text-green-600 font-semibold' : 'text-red-500 font-semibold'}>
                      {tp.result === 'WIN' ? 'W' : 'L'}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    }

    // BRACKET_PREDICTION (Pick6)
    if (picks.type === 'BRACKET_PREDICTION' && picks.teams) {
      const teamsArr = picks.teams as any[];
      const aliveCount = eliminatedTeamIds.size > 0 ? teamsArr.filter((tp: any) => !eliminatedTeamIds.has(tp.teamId)).length : 0;
      return (
        <>
          {eliminatedTeamIds.size > 0 && (
            <div className="flex items-center gap-2 text-xs text-gray-500 pb-1 mb-1">
              <span className="font-semibold text-green-600">{aliveCount} alive</span>
              <span>/</span>
              <span className="font-semibold text-red-600">{teamsArr.length - aliveCount} eliminated</span>
            </div>
          )}
          {teamsArr.map((tp: any) => {
            const isElim = eliminatedTeamIds.has(tp.teamId);
            return (
              <div key={tp.teamId} className={`flex items-center justify-between py-2 border-b last:border-0 ${isElim ? 'border-red-100' : 'border-green-100'}`}>
                <span className={isElim ? 'text-gray-400 line-through' : 'text-gray-900'}>
                  <span className={`font-bold ${isElim ? 'text-red-300' : 'text-gray-500'}`}>#{tp.seed}</span> {tp.teamName}
                </span>
                <div className="flex items-center gap-2">
                  {tp.region && <span className="text-xs text-gray-400">{tp.region}</span>}
                  {eliminatedTeamIds.size > 0 && (
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${isElim ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                      {isElim ? 'Out' : 'Alive'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </>
      );
    }

    // WEEKLY_LINEUP (NFL No-Repeat) — one block per filled week
    if (picks.type === 'WEEKLY_LINEUP' && picks.weeks) {
      const slotOrder = ['QB', 'RB1', 'RB2', 'WR1', 'WR2', 'TE', 'FLEX'];
      const weeks = Object.entries(picks.weeks as Record<string, any>)
        .filter(([, w]) => !w?.hidden && w?.positions && Object.keys(w.positions).length > 0)
        .sort(([a], [b]) => Number(a) - Number(b));
      if (weeks.length === 0) {
        return <p className="text-gray-500 text-sm">No weekly lineups set yet</p>;
      }
      return (
        <div className="space-y-4">
          {weeks.map(([weekNum, w]) => (
            <div key={weekNum}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-500 uppercase">Week {weekNum}</p>
                <span className="text-xs font-semibold text-emerald-700">
                  {Number(w.weekScore || 0).toFixed(1)} pts
                </span>
              </div>
              {slotOrder.map((slot) => {
                const p = w.positions[slot];
                return (
                  <div
                    key={slot}
                    className="flex justify-between items-center py-1 border-b border-gray-100 last:border-0"
                  >
                    <span className="text-xs font-medium text-gray-500 w-14">{slot}</span>
                    <span className={`flex-1 text-right ${p ? 'text-gray-900' : 'text-gray-400'}`}>
                      {p ? p.playerName : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      );
    }

    // F1_SEQUENTIAL_RACES
    if (picks.type === 'F1_SEQUENTIAL_RACES' && picks.picks) {
      return (picks.picks as any[]).map((rp: any) => (
        <div key={rp.raceId} className="py-2 border-b border-gray-200 last:border-0">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
            {rp.raceName || `Race ${rp.raceNumber}`}
          </p>
          {(rp.drivers as any[]).map((d: any) => (
            <div key={d.driverId} className="flex items-center gap-2 text-gray-900">
              <span>{d.driverName}</span>
              {d.teamName && <span className="text-xs text-gray-500">({d.teamName})</span>}
            </div>
          ))}
        </div>
      ));
    }

    // WORLD_CUP_BRACKET_PREDICTION (World Cup Pick 6)
    // Eliminated/scored info lives on the pick itself (picks.eliminatedTeams[],
    // picks.scoredRounds[teamId][]), not on a global cross-pool endpoint like NCAA.
    if (picks.type === 'WORLD_CUP_BRACKET_PREDICTION' && Array.isArray(picks.teams)) {
      const teamsArr = picks.teams as any[];
      const wcEliminated: string[] = picks.eliminatedTeams || [];
      const wcScoredRounds: Record<string, string[]> = picks.scoredRounds || {};
      const ROUND_LABEL: Record<string, string> = {
        GROUP: 'Group',
        R32: 'R32',
        R16: 'R16',
        QF: 'QF',
        SF: 'SF',
        FINAL: 'Final',
      };
      const tournamentStarted = wcEliminated.length > 0 || Object.keys(wcScoredRounds).length > 0;
      const aliveCount = tournamentStarted
        ? teamsArr.filter((tp: any) => !wcEliminated.includes(tp.teamId)).length
        : teamsArr.length;
      return (
        <>
          {tournamentStarted && (
            <div className="flex items-center gap-2 text-xs text-gray-500 pb-1 mb-1">
              <span className="font-semibold text-green-600">{aliveCount} alive</span>
              <span>/</span>
              <span className="font-semibold text-red-600">{teamsArr.length - aliveCount} eliminated</span>
            </div>
          )}
          {teamsArr.map((tp: any) => {
            const isElim = wcEliminated.includes(tp.teamId);
            const rounds = wcScoredRounds[tp.teamId] || [];
            const lastRound = rounds.length > 0 ? rounds[rounds.length - 1] : null;
            return (
              <div
                key={tp.teamId}
                className={`flex items-center justify-between py-2 border-b last:border-0 ${
                  isElim ? 'border-red-100' : tournamentStarted ? 'border-green-100' : 'border-gray-100'
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {tp.isoCode && <CountryFlag iso={tp.isoCode} alt={tp.teamName} size="sm" rounded />}
                  <span className={isElim ? 'text-gray-400 line-through' : 'text-gray-900'}>
                    <span className={`font-bold ${isElim ? 'text-red-300' : 'text-gray-500'}`}>Seed {tp.seed}</span>
                    {' · '}{tp.teamName}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {lastRound && (
                    <span className="text-xs font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                      Reached: {ROUND_LABEL[lastRound] || lastRound}
                    </span>
                  )}
                  {tournamentStarted && (
                    <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                      isElim ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                    }`}>
                      {isElim ? 'Out' : 'Alive'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </>
      );
    }

    // WORLD_CUP_SURVIVOR (World Cup Survivor — group stage 6 + knockout picks)
    if (picks.type === 'WORLD_CUP_SURVIVOR') {
      const groupTeams: any[] = picks.groupStageTeams || [];
      const knockoutPicks: Record<string, any> = picks.knockoutPicks || {};
      const status = picks.status || 'ALIVE';
      const eliminatedRound = picks.eliminatedRound;
      const ROUND_LABEL: Record<string, string> = {
        R32: 'Round of 32',
        R16: 'Round of 16',
        QF: 'Quarterfinals',
        SF: 'Semifinals',
        FINAL: 'Final',
      };
      const KNOCKOUT_ORDER = ['R32', 'R16', 'QF', 'SF', 'FINAL'];
      return (
        <>
          <div
            className={`mb-3 text-xs font-semibold inline-block px-2 py-0.5 rounded ${
              status === 'ALIVE' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
            }`}
          >
            Status: {status}
            {eliminatedRound && status === 'ELIMINATED' && (
              <> · {eliminatedRound === 'GROUP' ? 'Group Stage' : ROUND_LABEL[eliminatedRound] || eliminatedRound}</>
            )}
          </div>
          {groupTeams.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Phase 1 — Group Stage (need all 6)</p>
              {groupTeams.map((t: any) => {
                const advanced = t.advanced ?? 'PENDING';
                return (
                  <div
                    key={t.teamId}
                    className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {t.isoCode && <CountryFlag iso={t.isoCode} alt={t.teamName} size="sm" rounded />}
                      <span className={advanced === 'NO' ? 'text-gray-400 line-through' : 'text-gray-900'}>
                        Seed {t.seed} · {t.teamName}
                      </span>
                    </div>
                    {advanced === 'YES' && (
                      <span className="text-xs font-semibold text-emerald-600">✓ advanced</span>
                    )}
                    {advanced === 'NO' && (
                      <span className="text-xs font-semibold text-rose-600">✗ out</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Phase 2 — Knockout</p>
            {KNOCKOUT_ORDER.map((round) => {
              const pick = knockoutPicks[round];
              if (!pick) {
                return (
                  <div
                    key={round}
                    className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0 text-sm text-gray-400 italic"
                  >
                    <span>{ROUND_LABEL[round]}</span>
                    <span>— no pick yet —</span>
                  </div>
                );
              }
              const result = pick.result || 'PENDING';
              return (
                <div
                  key={round}
                  className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs uppercase tracking-wide text-gray-500 w-24">{ROUND_LABEL[round]}</span>
                    {pick.isoCode && <CountryFlag iso={pick.isoCode} alt={pick.teamName} size="sm" rounded />}
                    <span className={result === 'LOSS' ? 'text-gray-400 line-through' : 'text-gray-900'}>
                      {pick.teamName}
                    </span>
                  </div>
                  {result === 'WIN' && <span className="text-xs font-semibold text-emerald-600">✓ Win</span>}
                  {result === 'LOSS' && <span className="text-xs font-semibold text-rose-600">✗ Loss</span>}
                </div>
              );
            })}
          </div>
        </>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pool...</p>
        </div>
      </div>
    );
  }

  if (error || !pool) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md">
          <div className="text-red-600 text-center">
            <h3 className="text-lg font-semibold mb-2">Error Loading Pool</h3>
            <p className="text-gray-600 mb-4">{error || 'Pool not found'}</p>
            <Link
              to="/pools"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Back to Pools
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Allow joining if pool is open AND user hasn't hit max entries
  const hasEntries = myEntries.length > 0;
  const canAddEntry = pool.status === 'OPEN' && (
    !pool.maxEntries || myEntries.length < pool.maxEntries
  );
  const canJoin = canAddEntry;
  
  // Get comprehensive rules content for this pool type
  const rulesContent = getPoolRulesContent(pool.type);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Lineup Modal */}
        {selectedEntry && pool && (
          <LineupModal
            entry={selectedEntry}
            poolId={pool.id}
            onClose={() => setSelectedEntry(null)}
          />
        )}

        {/* Admin Edit Picks Modal */}
        {editingEntry && pool && currentUser?.role === 'ADMIN' && (
          <AdminEditPicksModal
            entry={editingEntry}
            poolId={pool.id}
            poolType={pool.type || 'CFP'}
            onClose={() => setEditingEntry(null)}
            onSuccess={() => {
              setEditingEntry(null);
              hasLoadedPool.current = null; // Allow reload
              loadPool(); // Reload pool data
            }}
          />
        )}

        {/* Back Link */}
        <Link
          to="/pools"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Pools
        </Link>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">{pool.name}</h1>
            <span className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${getStatusBadge(pool.status)}`}>
              {pool.status}
            </span>
          </div>
          
          {pool.description && (
            <p className="text-gray-600 mb-4">{pool.description}</p>
          )}

          {/* Pool Info Grid — 3 cols always, compact on mobile */}
          <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6">
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 p-2 md:p-4 rounded-lg">
              <p className="text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Deadline</p>
              <p className="font-bold text-gray-900 text-xs md:text-sm leading-tight">
                <span className="md:hidden">{new Date(pool.deadline).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}</span>
                <span className="hidden md:inline">{new Date(pool.deadline).toLocaleString()}</span>
              </p>
              <p className="text-[10px] md:text-xs text-blue-600 mt-1 md:mt-2 font-medium leading-tight">
                {formatDeadline(pool.deadline)}
              </p>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 p-2 md:p-4 rounded-lg">
              <p className="text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Entries</p>
              <p className="font-bold text-gray-900 text-lg md:text-2xl">{entries.length}</p>
              {pool.type === 'OLYMPIC_HOCKEY' && pool.uniqueLineups !== undefined && (
                <p className="text-[10px] md:text-xs text-purple-600 mt-1 md:mt-2 font-medium leading-tight">
                  {pool.uniqueLineups} unique
                </p>
              )}
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 p-2 md:p-4 rounded-lg">
              <p className="text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Entry Fee</p>
              <p className="font-bold text-gray-900 text-lg md:text-2xl">
                {pool.entryFee ? `${pool.entryFee}` : 'Free'}
              </p>
            </div>
          </div>

          {/* Payouts Section */}
          {pool.entryFee && Number(pool.entryFee) > 0 && entries.length > 0 && (() => {
            // Survivor-style winner-take-all pools (NCAA Knockout + World Cup Survivor)
            // share the same payout structure: 100% of the pot to the last entry standing.
            const isWinnerTakeAll =
              pool.type === 'NCAA_BASKETBALL_KNOCKOUT' || pool.type === 'WORLD_CUP_SURVIVOR';
            // Sort entries by score to determine winners
            const sortedEntries = [...entries].sort((a, b) => Number(b.score) - Number(a.score));
            const winner1st = sortedEntries[0];
            const winner2nd = sortedEntries[1];
            const winner3rd = sortedEntries[2];
            const isCompleted = pool.status === 'COMPLETED';
            const totalPrize = Number(pool.entryFee) * entries.length;

            // Knockout / Survivor = winner take all; other pools = 60/30/10
            if (isWinnerTakeAll) {
              return (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-6 mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-xl font-bold text-gray-900">Prize Pool</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3 md:gap-4">
                    <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-green-200">
                      <p className="text-[10px] md:text-xs font-semibold text-gray-600 uppercase mb-1 md:mb-2">Total Prize Pool</p>
                      <p className="text-xl md:text-2xl font-bold text-green-600">${totalPrize.toFixed(2)}</p>
                      <p className="text-[10px] md:text-xs text-gray-500 mt-1">{entries.length} × ${pool.entryFee}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-yellow-300">
                      <div className="flex items-center gap-2 mb-1 md:mb-2">
                        <span className="text-xl md:text-2xl">🏆</span>
                        <p className="text-[10px] md:text-xs font-semibold text-gray-600 uppercase">Winner Takes All</p>
                      </div>
                      <p className="text-xl md:text-2xl font-bold text-yellow-600">${totalPrize.toFixed(2)}</p>
                      {isCompleted && winner1st ? (
                        <div className="mt-1 md:mt-2 pt-1 md:pt-2 border-t border-yellow-200">
                          <p className="text-xs md:text-sm font-semibold text-gray-900 truncate">
                            {(winner1st as any).displayName || (winner1st as any).username || 'Winner'}
                          </p>
                          <p className="text-[10px] md:text-xs text-gray-500 truncate">{winner1st.entryName || 'Entry'}</p>
                        </div>
                      ) : (
                        <p className="text-[10px] md:text-xs text-gray-500 mt-1">Last entry standing</p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-4 text-center">
                    💡 If multiple survivors remain after the {pool.type === 'WORLD_CUP_SURVIVOR' ? 'Final' : 'Championship'}, they split the pot evenly
                  </p>
                </div>
              );
            }

            return (
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-xl font-bold text-gray-900">Prize Payouts</h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                  <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-green-200 col-span-2 md:col-span-1">
                    <p className="text-[10px] md:text-xs font-semibold text-gray-600 uppercase mb-1 md:mb-2">Total Prize Pool</p>
                    <p className="text-xl md:text-2xl font-bold text-green-600">${totalPrize.toFixed(2)}</p>
                    <p className="text-[10px] md:text-xs text-gray-500 mt-1">{entries.length} × ${pool.entryFee}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-yellow-300">
                    <div className="flex items-center gap-2 mb-1 md:mb-2">
                      <span className="text-xl md:text-2xl">🥇</span>
                      <p className="text-[10px] md:text-xs font-semibold text-gray-600 uppercase">1st</p>
                    </div>
                    <p className="text-lg md:text-2xl font-bold text-yellow-600">${(totalPrize * 0.6).toFixed(2)}</p>
                    {isCompleted && winner1st ? (
                      <div className="mt-1 md:mt-2 pt-1 md:pt-2 border-t border-yellow-200">
                        <p className="text-xs md:text-sm font-semibold text-gray-900 truncate">{(winner1st as any).displayName || (winner1st as any).username || 'Winner'}</p>
                        <p className="text-[10px] md:text-xs text-gray-500 truncate">{winner1st.entryName || 'Entry'}</p>
                      </div>
                    ) : (
                      <p className="text-[10px] md:text-xs text-gray-500 mt-1">60% of pool</p>
                    )}
                  </div>
                  <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-gray-300">
                    <div className="flex items-center gap-2 mb-1 md:mb-2">
                      <span className="text-xl md:text-2xl">🥈</span>
                      <p className="text-[10px] md:text-xs font-semibold text-gray-600 uppercase">2nd</p>
                    </div>
                    <p className="text-lg md:text-2xl font-bold text-gray-600">${(totalPrize * 0.3).toFixed(2)}</p>
                    {isCompleted && winner2nd ? (
                      <div className="mt-1 md:mt-2 pt-1 md:pt-2 border-t border-gray-200">
                        <p className="text-xs md:text-sm font-semibold text-gray-900 truncate">{(winner2nd as any).displayName || (winner2nd as any).username || 'Winner'}</p>
                        <p className="text-[10px] md:text-xs text-gray-500 truncate">{winner2nd.entryName || 'Entry'}</p>
                      </div>
                    ) : (
                      <p className="text-[10px] md:text-xs text-gray-500 mt-1">30% of pool</p>
                    )}
                  </div>
                  <div className="bg-white rounded-lg p-3 md:p-4 shadow-sm border border-orange-300 col-span-2 md:col-span-1">
                    <div className="flex items-center gap-2 mb-1 md:mb-2">
                      <span className="text-xl md:text-2xl">🥉</span>
                      <p className="text-[10px] md:text-xs font-semibold text-gray-600 uppercase">3rd</p>
                    </div>
                    <p className="text-lg md:text-2xl font-bold text-orange-600">${(totalPrize * 0.1).toFixed(2)}</p>
                    {isCompleted && winner3rd ? (
                      <div className="mt-1 md:mt-2 pt-1 md:pt-2 border-t border-orange-200">
                        <p className="text-xs md:text-sm font-semibold text-gray-900 truncate">{(winner3rd as any).displayName || (winner3rd as any).username || 'Winner'}</p>
                        <p className="text-[10px] md:text-xs text-gray-500 truncate">{winner3rd.entryName || 'Entry'}</p>
                      </div>
                    ) : (
                      <p className="text-[10px] md:text-xs text-gray-500 mt-1">10% of pool</p>
                    )}
                  </div>
                </div>

                <p className="text-xs text-gray-600 mt-4 text-center">
                  💡 Ties split prize money evenly among tied positions
                </p>
              </div>
            );
          })()}

          {/* Action Buttons */}
          <div className="flex gap-3 flex-wrap">
            {canJoin && (
              <Link
                to={pool.type === 'NCAA_BASKETBALL_KNOCKOUT'
                  ? `/pools/${poolId}/knockout`
                  : pool.type === 'NCAA_BASKETBALL_PICK6'
                  ? `/pools/${poolId}/pick6`
                  : pool.type === 'FORMULA_1'
                  ? `/pools/${poolId}/f1`
                  : pool.type === 'WORLD_CUP_PICK6'
                  ? `/pools/${poolId}/wc-pick6`
                  : pool.type === 'WORLD_CUP_SURVIVOR'
                  ? `/pools/${poolId}/wc-survivor`
                  : pool.type === 'NFL_NO_REPEAT'
                  ? `/pools/${poolId}/nfl-no-repeat`
                  : `/pools/${poolId}/join`}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-md hover:shadow-lg"
              >
                {hasEntries ? 'Add Another Entry' : 'Join This Pool'}
              </Link>
            )}
            {/* Knockout: prominent "Make Today's Picks" button after pool locks */}
            {hasEntries && pool.status !== 'OPEN' && pool.type === 'NCAA_BASKETBALL_KNOCKOUT' && pool.status !== 'COMPLETED' && (
              myEntries.map(myEntry => (
                <Link
                  key={`pick-${myEntry.id}`}
                  to={`/pools/${poolId}/knockout/${myEntry.id}`}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-md hover:shadow-lg"
                >
                  Make Today's Picks{myEntries.length > 1 ? ` (${myEntry.entryName || 'Entry'})` : ''}
                </Link>
              ))
            )}
            {/* World Cup Survivor: prominent "Make Phase 2 Picks" button after pool locks.
                Renders one button per surviving entry. Button label adapts to the entry's
                state (WAITING / READY / ELIMINATED) so the user sees their situation at
                a glance from the pool page. */}
            {hasEntries && pool.status !== 'OPEN' && pool.type === 'WORLD_CUP_SURVIVOR' && pool.status !== 'COMPLETED' && (
              myEntries.map((myEntry) => {
                const survivorPicks = (myEntry as any).picks;
                const status = survivorPicks?.status || 'ALIVE';
                const groupPicks = Array.isArray(survivorPicks?.groupStageTeams)
                  ? survivorPicks.groupStageTeams
                  : [];
                const allAdvanced = groupPicks.length > 0 && groupPicks.every((t: any) => t.advanced === 'YES');
                const someStillPending = groupPicks.some((t: any) => !t.advanced || t.advanced === 'PENDING');
                const isEntryEliminated = status === 'ELIMINATED';
                const entryLabel = myEntries.length > 1 ? ` (${myEntry.entryName || 'Entry'})` : '';
                const label = isEntryEliminated
                  ? `Eliminated — view picks${entryLabel}`
                  : allAdvanced
                  ? `Make Phase 2 Picks${entryLabel}`
                  : `View Entry${entryLabel}${someStillPending ? ' — waiting on group stage' : ''}`;
                const className = isEntryEliminated
                  ? 'bg-rose-100 hover:bg-rose-200 text-rose-800 border border-rose-300 font-semibold py-3 px-6 rounded-lg transition-colors shadow-sm'
                  : allAdvanced
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-md hover:shadow-lg'
                  : 'bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 font-semibold py-3 px-6 rounded-lg transition-colors shadow-sm';
                return (
                  <Link
                    key={`wcsurv-${myEntry.id}`}
                    to={`/pools/${poolId}/wc-survivor/${myEntry.id}`}
                    className={className}
                  >
                    {label}
                  </Link>
                );
              })
            )}
            {/* NFL No-Repeat: prominent "Set Weekly Lineup" button once the season is underway */}
            {hasEntries && pool.status !== 'OPEN' && pool.type === 'NFL_NO_REPEAT' && pool.status !== 'COMPLETED' && (
              <Link
                to={`/pools/${poolId}/nfl-no-repeat`}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-md hover:shadow-lg"
              >
                Set Weekly Lineup
              </Link>
            )}
            {hasEntries && (
              <button
                onClick={() => setShowMyPicks(!showMyPicks)}
                className="bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-300 font-semibold py-3 px-6 rounded-lg transition-colors shadow-sm"
              >
                {showMyPicks ? 'Hide My Entries' : `View My Entries (${myEntries.length})`}
              </button>
            )}
            {currentUser?.role === 'ADMIN' && pool.status === 'IN_PROGRESS' && (
              <button
                onClick={handleCompletePool}
                disabled={completing}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {completing ? 'Marking Complete...' : 'Mark Pool Complete'}
              </button>
            )}
          </div>
        </div>

        {/* My Entries Section */}
        {hasEntries && showMyPicks && (
          <div className="space-y-4 mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              My {myEntries.length === 1 ? 'Entry' : `Entries (${myEntries.length})`}
            </h2>
            {myEntries.map((myEntry, idx) => (
              <div key={myEntry.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">
                    {myEntry.entryName || `Entry ${idx + 1}`}
                  </h3>
                  <div className="flex items-center gap-2">
                    {pool.status === 'OPEN' && (
                      <>
                        <Link
                          to={pool.type === 'NCAA_BASKETBALL_KNOCKOUT'
                            ? `/pools/${poolId}/knockout/${myEntry.id}`
                            : pool.type === 'NCAA_BASKETBALL_PICK6'
                            ? `/pools/${poolId}/pick6/${myEntry.id}`
                            : pool.type === 'FORMULA_1'
                            ? `/pools/${poolId}/f1/${myEntry.id}`
                            : pool.type === 'WORLD_CUP_PICK6'
                            ? `/pools/${poolId}/wc-pick6/${myEntry.id}`
                            : pool.type === 'WORLD_CUP_SURVIVOR'
                            ? `/pools/${poolId}/wc-survivor/${myEntry.id}`
                            : pool.type === 'NFL_NO_REPEAT'
                            ? `/pools/${poolId}/nfl-no-repeat`
                            : `/pools/${poolId}/edit/${myEntry.id}`}
                          className="text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                        >
                          Edit Picks
                        </Link>
                        <button
                          onClick={() => handleDeleteEntry(myEntry)}
                          disabled={deleting === myEntry.id}
                          className="text-sm bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deleting === myEntry.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </>
                    )}
                    {/* Knockout: allow adding daily picks even after pool locks */}
                    {pool.status !== 'OPEN' && pool.type === 'NCAA_BASKETBALL_KNOCKOUT' && (
                      <Link
                        to={`/pools/${poolId}/knockout/${myEntry.id}`}
                        className="text-sm bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                      >
                        Add Today's Picks
                      </Link>
                    )}
                  </div>
                </div>

                {/* Payment Status Badge (Payment Tracking v2) */}
                {myEntry.paymentStatus && myEntry.paymentStatus !== 'NOT_REQUIRED' && (
                  <div className={`mb-4 p-3 rounded-lg border-2 flex items-center gap-2 ${
                    myEntry.paymentStatus === 'PAID'
                      ? 'bg-green-50 border-green-200 text-green-800'
                      : 'bg-amber-50 border-amber-300 text-amber-800'
                  }`}>
                    <span className="text-lg">
                      {myEntry.paymentStatus === 'PAID' ? '✅' : '💵'}
                    </span>
                    <div className="flex-1">
                      <span className="font-semibold">
                        {myEntry.paymentStatus === 'PAID' ? 'Payment received' : 'Entry fee unpaid'}
                      </span>
                      {myEntry.paymentStatus === 'PAID' && myEntry.paymentMethod && (
                        <span className="text-sm ml-2 opacity-70">
                          via {PAYMENT_METHOD_LABELS[myEntry.paymentMethod] || myEntry.paymentMethod}
                        </span>
                      )}
                    </div>
                    {myEntry.paymentStatus !== 'PAID' && (
                      <Link
                        to="/settle-up"
                        className="shrink-0 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Settle up →
                      </Link>
                    )}
                  </div>
                )}

                {pool.type === 'NCAA_BASKETBALL_KNOCKOUT' ? (() => {
                  const koPicks = myEntry.picks as any;
                  const koStatus = koPicks?.status || 'ALIVE';
                  const koBuyBacks = koPicks?.buyBackCount || 0;
                  const canBuyBack = koStatus === 'ELIMINATED' && koBuyBacks < 3 && pool.status !== 'COMPLETED';
                  return (
                    <div className={`p-5 rounded-lg mb-4 shadow-sm border-2 ${
                      koStatus === 'ALIVE' ? 'bg-gradient-to-br from-green-50 to-green-100 border-green-200' :
                      koStatus === 'BOUGHT_BACK' ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200' :
                      'bg-gradient-to-br from-red-50 to-red-100 border-red-200'
                    }`}>
                      <p className="text-sm font-medium text-gray-700 mb-1">Status</p>
                      <p className={`text-3xl font-bold ${
                        koStatus === 'ALIVE' ? 'text-green-600' :
                        koStatus === 'BOUGHT_BACK' ? 'text-orange-600' :
                        'text-red-600'
                      }`}>
                        {koStatus === 'ALIVE' ? '✓ Alive' :
                         koStatus === 'BOUGHT_BACK' ? '🔄 Bought Back' :
                         '✗ Eliminated'}
                      </p>
                      {koBuyBacks > 0 && (
                        <p className="text-sm text-gray-600 mt-1">{koBuyBacks}/3 buy-backs used</p>
                      )}
                      {canBuyBack && (
                        <BuyBackButton poolId={pool.id} entryId={myEntry.id} onSuccess={() => {
                          hasLoadedPool.current = null;
                          loadPool();
                          hasLoadedMyEntry.current = null;
                          loadMyEntries();
                        }} />
                      )}
                    </div>
                  );
                })() : (
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 p-5 rounded-lg mb-4 shadow-sm">
                  <p className="text-sm font-medium text-blue-900 mb-1">Current Score</p>
                  <p className="text-3xl font-bold text-blue-600">{myEntry.score}</p>
                </div>
                )}
                <div className="border-t border-gray-200 pt-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Your Picks</h3>
                  <div className="space-y-1">
                    {formatPicks(myEntry.picks)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tournament Bracket Section (NCAA Basketball pools only) */}
        {['NCAA_BASKETBALL_PICK6', 'NCAA_BASKETBALL_KNOCKOUT'].includes(pool.type) && (
          <div className="mb-6">
            <button
              onClick={() => setShowBracket(!showBracket)}
              className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-300 font-semibold py-3 px-6 rounded-lg transition-colors shadow-sm mb-4"
            >
              <span className="text-xl">🏀</span>
              {showBracket ? 'Hide Tournament Bracket' : 'View Tournament Bracket'}
              <svg
                className={`w-5 h-5 transition-transform ${showBracket ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showBracket && poolId && (
              <>
                <TournamentBracketView poolId={poolId} />
                {bracketTeams.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Seedings</h3>
                    <BracketVisualization
                      teams={bracketTeams}
                      onTeamClick={undefined}
                      showResults={pool.status !== 'OPEN'}
                      compact={true}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Leaderboard */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            {pool.type === 'NCAA_BASKETBALL_KNOCKOUT' || pool.type === 'WORLD_CUP_SURVIVOR'
              ? 'Survivor Standings'
              : 'Leaderboard'}
          </h2>

          {/* Knockout Pool Summary */}
          {pool.type === 'NCAA_BASKETBALL_KNOCKOUT' && entries.length > 0 && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-700">
                  {entries.filter((e: any) => e.knockoutStatus === 'ALIVE' || !e.knockoutStatus).length}
                </div>
                <div className="text-sm text-green-600 font-medium">Still Alive</div>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-orange-700">
                  {entries.filter((e: any) => e.knockoutStatus === 'BOUGHT_BACK').length}
                </div>
                <div className="text-sm text-orange-600 font-medium">Bought Back</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-700">
                  {entries.filter((e: any) => e.knockoutStatus === 'ELIMINATED').length}
                </div>
                <div className="text-sm text-red-600 font-medium">Eliminated</div>
              </div>
            </div>
          )}

          {/* World Cup Survivor Pool Summary — no buy-backs in Survivor, so only 2 tiles */}
          {pool.type === 'WORLD_CUP_SURVIVOR' && entries.length > 0 && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-emerald-700">
                  {
                    entries.filter(
                      (e: any) => (e.picks?.status ?? 'ALIVE') === 'ALIVE',
                    ).length
                  }
                </div>
                <div className="text-sm text-emerald-600 font-medium">Still Alive</div>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-rose-700">
                  {entries.filter((e: any) => e.picks?.status === 'ELIMINATED').length}
                </div>
                <div className="text-sm text-rose-600 font-medium">Eliminated</div>
              </div>
            </div>
          )}

          {entries.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No entries yet. Be the first to join!</p>
            </div>
          ) : (
            <>
              {/* MOBILE CARD LAYOUT — World Cup pools only.
                  The existing table cuts off the Status / Picks / Score columns on
                  narrow screens. On mobile we render each entry as a stacked card
                  so all info (status badge + flag picks + actions) is visible
                  without horizontal scrolling. Desktop still gets the table below. */}
              {(pool.type === 'WORLD_CUP_PICK6' || pool.type === 'WORLD_CUP_SURVIVOR') && (
                <div className="md:hidden space-y-2">
                  {entries
                    .slice()
                    .sort((a, b) => Number(b.score) - Number(a.score))
                    .map((entry, index) => {
                      const isMyEntry = myEntries.some((me) => me.id === entry.id);
                      const entryPicks = (entry as any).picks;
                      const picksHidden =
                        pool.status === 'OPEN' && !isMyEntry && currentUser?.role !== 'ADMIN';

                      // Build the flag list + status badge per pool type
                      let statusBadge: React.ReactNode = null;
                      let scoreEl: React.ReactNode = null;
                      let pickFlags: { teamId: string; teamName: string; seed?: number; isoCode?: string; out?: boolean }[] = [];
                      let isEliminated = false;

                      if (pool.type === 'WORLD_CUP_SURVIVOR') {
                        const survivorStatus = entryPicks?.status || 'ALIVE';
                        const elimRound = entryPicks?.eliminatedRound;
                        isEliminated = survivorStatus === 'ELIMINATED';
                        statusBadge = (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              survivorStatus === 'ALIVE'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-rose-100 text-rose-800'
                            }`}
                          >
                            {survivorStatus === 'ALIVE' ? '✓ Alive' : '✗ Eliminated'}
                            {survivorStatus === 'ELIMINATED' && elimRound && (
                              <span className="ml-1 opacity-75">
                                ({elimRound === 'GROUP' ? 'GS' : elimRound})
                              </span>
                            )}
                          </span>
                        );
                        const groupPicks = Array.isArray(entryPicks?.groupStageTeams)
                          ? entryPicks.groupStageTeams
                          : [];
                        const knockoutPicks: any[] = entryPicks?.knockoutPicks
                          ? Object.values(entryPicks.knockoutPicks)
                          : [];
                        pickFlags = [...groupPicks, ...knockoutPicks].map((p: any) => ({
                          teamId: p?.teamId,
                          teamName: p?.teamName,
                          seed: p?.seed,
                          isoCode: p?.isoCode,
                          out: p?.advanced === 'NO' || p?.result === 'LOSS',
                        }));
                      } else {
                        // WORLD_CUP_PICK6
                        const teamPicks: any[] = Array.isArray(entryPicks?.teams) ? entryPicks.teams : [];
                        const eliminated: string[] = entryPicks?.eliminatedTeams || [];
                        pickFlags = teamPicks.map((p: any) => ({
                          teamId: p?.teamId,
                          teamName: p?.teamName,
                          seed: p?.seed,
                          isoCode: p?.isoCode,
                          out: eliminated.includes(p?.teamId),
                        }));
                        scoreEl = (
                          <span className="text-sm font-bold text-gray-900">
                            {Number(entry.score).toFixed(2)}
                            <span className="text-[10px] text-gray-500 font-normal ml-1">pts</span>
                          </span>
                        );
                      }

                      return (
                        <div
                          key={entry.id}
                          className={`border rounded-lg p-3 ${
                            isMyEntry
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-white border-gray-200'
                          } ${isEliminated ? 'opacity-60' : ''}`}
                        >
                          {/* Header row: rank + entry name + status/score */}
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-xs font-semibold text-gray-500 flex-shrink-0">
                                #{index + 1}
                              </span>
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {entry.entryName || 'Entry'}
                              </span>
                              {isMyEntry && (
                                <span className="text-[10px] text-blue-600 font-semibold flex-shrink-0">(You)</span>
                              )}
                            </div>
                            <div className="flex-shrink-0">{statusBadge || scoreEl}</div>
                          </div>
                          {/* User line */}
                          <p className="text-[11px] text-gray-500 mb-2 truncate">
                            {(entry as any).displayName || (entry as any).username || 'Anonymous'}
                          </p>
                          {/* Current-round pick chip — only shown for WC Survivor in Phase 2.
                              Mirrors the desktop "Current Round Pick" column: visible if backend
                              didn't redact, else "Hidden until kickoff". */}
                          {pool.type === 'WORLD_CUP_SURVIVOR' && wcActiveKnockoutRound && !isEliminated && (() => {
                            const pickForRound: any = entryPicks?.knockoutPicks?.[wcActiveKnockoutRound.backendKey];
                            const firstKickoff = wcActiveKnockoutRound.firstKickoff;
                            const roundHasStarted = firstKickoff ? new Date() >= firstKickoff : false;
                            return (
                              <div className="mb-2 flex items-center gap-1.5 flex-wrap">
                                <span className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
                                  {wcActiveKnockoutRound.label}:
                                </span>
                                {pickForRound?.isoCode ? (
                                  <span
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium border ${
                                      pickForRound.result === 'WIN'
                                        ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                                        : pickForRound.result === 'LOSS'
                                        ? 'bg-rose-50 border-rose-300 text-rose-900'
                                        : 'bg-white border-gray-200 text-gray-800'
                                    }`}
                                  >
                                    <CountryFlag iso={pickForRound.isoCode} alt={pickForRound.teamName} size="xs" rounded />
                                    <span>{pickForRound.shortName || pickForRound.teamName}</span>
                                    {pickForRound.result === 'WIN' && <span>✓</span>}
                                    {pickForRound.result === 'LOSS' && <span>✗</span>}
                                  </span>
                                ) : (
                                  <span className="text-[11px] text-gray-400 italic">
                                    {roundHasStarted ? 'No pick' : '🔒 Hidden until kickoff'}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                          {/* Picks (flags) */}
                          {picksHidden ? (
                            <p className="text-[11px] text-gray-400 italic">Hidden until first match</p>
                          ) : pickFlags.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {pickFlags.map(
                                (p, i) =>
                                  p.isoCode && (
                                    <span
                                      key={`${p.teamId}-${i}`}
                                      title={`${p.teamName} (Seed ${p.seed})${p.out ? ' — out' : ''}`}
                                      className={p.out ? 'opacity-40' : ''}
                                    >
                                      <CountryFlag iso={p.isoCode} alt={p.teamName} size="sm" rounded />
                                    </span>
                                  ),
                              )}
                            </div>
                          ) : (
                            <p className="text-[11px] text-gray-400">No picks</p>
                          )}
                          {/* Action button — only if visible per privacy rules */}
                          {(pool.status !== 'OPEN' || isMyEntry || currentUser?.role === 'ADMIN') && (
                            <div className="mt-2 flex gap-2">
                              <button
                                onClick={() => setSelectedEntry(entry)}
                                className="text-xs font-semibold text-blue-600 hover:text-blue-800"
                              >
                                View full picks →
                              </button>
                              {isMyEntry && pool.status === 'OPEN' && (
                                <Link
                                  to={
                                    pool.type === 'WORLD_CUP_PICK6'
                                      ? `/pools/${poolId}/wc-pick6/${entry.id}`
                                      : `/pools/${poolId}/wc-survivor/${entry.id}`
                                  }
                                  className="text-xs font-semibold text-emerald-600 hover:text-emerald-800"
                                >
                                  Edit
                                </Link>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}

              {/* MOBILE CARD LAYOUT — NCAA Knockout (8-col table is the worst mobile
                  offender; status + today's picks are the critical data). */}
              {pool.type === 'NCAA_BASKETBALL_KNOCKOUT' && (
                <div className="md:hidden space-y-2">
                  {entries
                    .slice()
                    .sort((a, b) => Number(b.score) - Number(a.score))
                    .map((entry, index) => {
                      const isMyEntry = myEntries.some((me) => me.id === entry.id);
                      const knockoutStatus = (entry as any).knockoutStatus || 'ALIVE';
                      const buyBackCount = (entry as any).buyBackCount || 0;
                      const daysPlayed = (entry as any).daysPlayed || 0;
                      const winningPicks = (entry as any).winningPicks || 0;
                      const picksHidden =
                        pool.status === 'OPEN' && !isMyEntry && currentUser?.role !== 'ADMIN';
                      const isEliminated = knockoutStatus === 'ELIMINATED';
                      // Latest day's picks for the inline summary
                      const entryPicks = (entry as any).picks;
                      let todayTeams: { teamName: string; seed: number; result?: string }[] = [];
                      if (entryPicks?.type === 'DAILY_PICKS' && entryPicks.picks) {
                        const dates = Object.keys(entryPicks.picks).sort();
                        const latestDate = dates[dates.length - 1];
                        if (latestDate) {
                          todayTeams = (entryPicks.picks[latestDate] || []).map((p: any) => ({
                            teamName: p.teamName,
                            seed: p.seed,
                            result: p.result,
                          }));
                        }
                      }
                      return (
                        <div
                          key={entry.id}
                          className={`border rounded-lg p-3 ${
                            isMyEntry
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-white border-gray-200'
                          } ${isEliminated ? 'opacity-60' : ''}`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-xs font-semibold text-gray-500 flex-shrink-0">
                                #{index + 1}
                              </span>
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {entry.entryName || 'Entry'}
                              </span>
                              {isMyEntry && (
                                <span className="text-[10px] text-blue-600 font-semibold flex-shrink-0">(You)</span>
                              )}
                            </div>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${
                                knockoutStatus === 'ALIVE'
                                  ? 'bg-green-100 text-green-800'
                                  : knockoutStatus === 'BOUGHT_BACK'
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {knockoutStatus === 'ALIVE'
                                ? '✓ Alive'
                                : knockoutStatus === 'BOUGHT_BACK'
                                ? `🔄 Back (${buyBackCount}/3)`
                                : '✗ Out'}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-500 mb-2 truncate">
                            {(entry as any).displayName || (entry as any).username || 'Anonymous'}
                            <span className="ml-2">· {daysPlayed} days · {winningPicks} wins</span>
                          </p>
                          {picksHidden ? (
                            <p className="text-[11px] text-gray-400 italic">Hidden until games start</p>
                          ) : todayTeams.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {todayTeams.map((t, i) => (
                                <span
                                  key={i}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${
                                    t.result === 'WIN'
                                      ? 'bg-green-100 text-green-800'
                                      : t.result === 'LOSS'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  ({t.seed}) {t.teamName}
                                  {t.result === 'WIN' && ' ✓'}
                                  {t.result === 'LOSS' && ' ✗'}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-[11px] text-gray-400">No picks for latest day</p>
                          )}
                          {(pool.status !== 'OPEN' || isMyEntry || currentUser?.role === 'ADMIN') && (
                            <button
                              onClick={() => setSelectedEntry(entry)}
                              className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-800"
                            >
                              View all picks →
                            </button>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}

              {/* MOBILE CARD LAYOUT — generic for remaining pool types
                  (CFP / NFL / NHL / NBA / Olympic / Four Nations / F1 / NCAA Pick 6).
                  These pools' tables are only 5 columns wide so they survive
                  horizontally, but the heavy padding wastes mobile space.
                  Compact card with rank + entry + score + user + action. */}
              {pool.type !== 'WORLD_CUP_PICK6' &&
                pool.type !== 'WORLD_CUP_SURVIVOR' &&
                pool.type !== 'NCAA_BASKETBALL_KNOCKOUT' && (
                  <div className="md:hidden space-y-2">
                    {entries
                      .slice()
                      .sort((a, b) => Number(b.score) - Number(a.score))
                      .map((entry, index) => {
                        const isMyEntry = myEntries.some((me) => me.id === entry.id);
                        return (
                          <div
                            key={entry.id}
                            className={`border rounded-lg p-3 ${
                              isMyEntry ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-xs font-semibold text-gray-500 flex-shrink-0">
                                  #{index + 1}
                                </span>
                                <span className="text-sm font-medium text-gray-900 truncate">
                                  {entry.entryName || 'Entry'}
                                </span>
                                {isMyEntry && (
                                  <span className="text-[10px] text-blue-600 font-semibold flex-shrink-0">(You)</span>
                                )}
                              </div>
                              <span className="text-sm font-bold text-gray-900 flex-shrink-0">
                                {Number(entry.score).toFixed(2)}
                                <span className="text-[10px] text-gray-500 font-normal ml-1">pts</span>
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-[11px] text-gray-500 truncate">
                                {(entry as any).displayName || (entry as any).username || 'Anonymous'}
                              </p>
                              {(pool.status !== 'OPEN' || isMyEntry || currentUser?.role === 'ADMIN') ? (
                                <button
                                  onClick={() => setSelectedEntry(entry)}
                                  className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex-shrink-0"
                                >
                                  View picks →
                                </button>
                              ) : (
                                <span className="text-[10px] text-gray-400 italic flex-shrink-0">
                                  Picks hidden
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}

              {/* DESKTOP TABLE — hidden on mobile for all pool types now that
                  every pool has a dedicated mobile card layout above. */}
              <div className="overflow-x-auto hidden md:block">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rank
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entry
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    {pool.type === 'NCAA_BASKETBALL_KNOCKOUT' ? (
                      <>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Today's Picks
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Days
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Wins
                        </th>
                      </>
                    ) : pool.type === 'WORLD_CUP_SURVIVOR' ? (
                      <>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        {/* Current Round column — only shown once we're in Phase 2 (a knockout round is active).
                            Header uses the resolved round name (e.g. "Round of 32 Pick") so users know which round
                            the column refers to without consulting the bracket. */}
                        {wcActiveKnockoutRound && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {wcActiveKnockoutRound.label} Pick
                          </th>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          All Picks
                        </th>
                      </>
                    ) : pool.type === 'WORLD_CUP_PICK6' ? (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Picks
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Score
                        </th>
                      </>
                    ) : (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Score
                      </th>
                    )}
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {entries
                    .sort((a, b) => Number(b.score) - Number(a.score))
                    .map((entry, index) => {
                      const isMyEntry = myEntries.some(me => me.id === entry.id);
                      const knockoutStatus = (entry as any).knockoutStatus || 'ALIVE';
                      const daysPlayed = (entry as any).daysPlayed || 0;
                      const winningPicks = (entry as any).winningPicks || 0;
                      const buyBackCount = (entry as any).buyBackCount || 0;

                      return (
                        <tr
                          key={entry.id}
                          className={`${isMyEntry ? 'bg-blue-50 font-medium' : ''} ${
                            (pool.type === 'NCAA_BASKETBALL_KNOCKOUT' && knockoutStatus === 'ELIMINATED') ||
                            (pool.type === 'WORLD_CUP_SURVIVOR' && (entry as any).picks?.status === 'ELIMINATED')
                              ? 'opacity-60'
                              : ''
                          }`}
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            #{index + 1}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {entry.entryName || 'Entry'}
                            {isMyEntry && (
                              <span className="ml-2 text-xs text-blue-600">(You)</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {(entry as any).displayName || (entry as any).username || 'Anonymous'}
                          </td>
                          {pool.type === 'NCAA_BASKETBALL_KNOCKOUT' ? (() => {
                            // Extract the most recent day's picks for inline display
                            const entryPicks = (entry as any).picks;
                            let todayTeams: { teamName: string; seed: number; result?: string }[] = [];
                            if (entryPicks?.type === 'DAILY_PICKS' && entryPicks.picks) {
                              const dates = Object.keys(entryPicks.picks).sort();
                              const latestDate = dates[dates.length - 1];
                              if (latestDate) {
                                todayTeams = (entryPicks.picks[latestDate] || []).map((p: any) => ({
                                  teamName: p.teamName,
                                  seed: p.seed,
                                  result: p.result,
                                }));
                              }
                            }
                            return (
                              <>
                                <td className="px-4 py-4 whitespace-nowrap text-center">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    knockoutStatus === 'ALIVE'
                                      ? 'bg-green-100 text-green-800'
                                      : knockoutStatus === 'BOUGHT_BACK'
                                      ? 'bg-orange-100 text-orange-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {knockoutStatus === 'ALIVE' ? '✓ Alive' :
                                     knockoutStatus === 'BOUGHT_BACK' ? `🔄 Back (${buyBackCount}/3)` :
                                     '✗ Out'}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-sm">
                                  {pool.status === 'OPEN' && !isMyEntry && currentUser?.role !== 'ADMIN' ? (
                                    <span className="text-gray-400 text-xs">Hidden</span>
                                  ) : todayTeams.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {todayTeams.map((t, i) => (
                                        <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                          t.result === 'WIN' ? 'bg-green-100 text-green-800' :
                                          t.result === 'LOSS' ? 'bg-red-100 text-red-800' :
                                          'bg-gray-100 text-gray-700'
                                        }`}>
                                          ({t.seed}) {t.teamName}
                                          {t.result === 'WIN' && ' ✓'}
                                          {t.result === 'LOSS' && ' ✗'}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs">No picks</span>
                                  )}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900">
                                  {daysPlayed}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-center text-sm font-medium text-green-600">
                                  {winningPicks}
                                </td>
                              </>
                            );
                          })() : pool.type === 'WORLD_CUP_SURVIVOR' ? (() => {
                            const entryPicks = (entry as any).picks;
                            const survivorStatus = entryPicks?.status || 'ALIVE';
                            const eliminatedRound = entryPicks?.eliminatedRound;
                            const groupPicks = Array.isArray(entryPicks?.groupStageTeams)
                              ? entryPicks.groupStageTeams
                              : [];
                            const knockoutPicks: any[] = entryPicks?.knockoutPicks
                              ? Object.values(entryPicks.knockoutPicks)
                              : [];
                            const allPicks = [...groupPicks, ...knockoutPicks];
                            const picksHidden =
                              pool.status === 'OPEN' && !isMyEntry && currentUser?.role !== 'ADMIN';

                            // Current round pick: the entry's pick for this round, IF present
                            // in the response. Backend's per-round privacy gate redacts the
                            // pick from other users until the round's first match kicks off,
                            // so its presence/absence is the source of truth for visibility.
                            // Exception: a user always sees their own pick (it lives in their
                            // own entry record), and admins always see everyone's.
                            const activeRoundKey = wcActiveKnockoutRound?.backendKey;
                            const currentRoundPick: any = activeRoundKey
                              ? entryPicks?.knockoutPicks?.[activeRoundKey]
                              : null;
                            const firstKickoff = wcActiveKnockoutRound?.firstKickoff || null;
                            const roundHasStarted = firstKickoff ? new Date() >= firstKickoff : false;
                            const kickoffTooltip = firstKickoff
                              ? `First match: ${firstKickoff.toLocaleString(undefined, {
                                  weekday: 'short', month: 'short', day: 'numeric',
                                  hour: 'numeric', minute: '2-digit',
                                })}`
                              : '';

                            return (
                              <>
                                <td className="px-4 py-4 whitespace-nowrap text-center">
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      survivorStatus === 'ALIVE'
                                        ? 'bg-emerald-100 text-emerald-800'
                                        : 'bg-rose-100 text-rose-800'
                                    }`}
                                  >
                                    {survivorStatus === 'ALIVE' ? '✓ Alive' : '✗ Eliminated'}
                                    {survivorStatus === 'ELIMINATED' && eliminatedRound && (
                                      <span className="ml-1 opacity-75">
                                        ({eliminatedRound === 'GROUP' ? 'GS' : eliminatedRound})
                                      </span>
                                    )}
                                  </span>
                                </td>
                                {/* Current round pick cell */}
                                {wcActiveKnockoutRound && (
                                  <td className="px-4 py-4 text-sm">
                                    {survivorStatus === 'ELIMINATED' ? (
                                      <span className="text-gray-400 text-xs">—</span>
                                    ) : currentRoundPick?.isoCode ? (
                                      <span
                                        title={`${currentRoundPick.teamName} (Seed ${currentRoundPick.seed})${
                                          currentRoundPick.result === 'WIN' ? ' — won'
                                            : currentRoundPick.result === 'LOSS' ? ' — lost' : ''
                                        }`}
                                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium border ${
                                          currentRoundPick.result === 'WIN'
                                            ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                                            : currentRoundPick.result === 'LOSS'
                                            ? 'bg-rose-50 border-rose-300 text-rose-900'
                                            : 'bg-white border-gray-200 text-gray-800'
                                        }`}
                                      >
                                        <CountryFlag iso={currentRoundPick.isoCode} alt={currentRoundPick.teamName} size="xs" rounded />
                                        <span className="truncate max-w-[120px]">
                                          {currentRoundPick.shortName || currentRoundPick.teamName}
                                        </span>
                                        {currentRoundPick.result === 'WIN' && <span>✓</span>}
                                        {currentRoundPick.result === 'LOSS' && <span>✗</span>}
                                      </span>
                                    ) : (
                                      // No pick present: either the round hasn't started yet
                                      // (backend redacted) or the user hasn't made a pick.
                                      // Disambiguate by checking kickoff time.
                                      <span
                                        className="text-gray-400 text-xs italic"
                                        title={kickoffTooltip}
                                      >
                                        {roundHasStarted ? 'No pick' : '🔒 Hidden until kickoff'}
                                      </span>
                                    )}
                                  </td>
                                )}
                                <td className="px-4 py-4 text-sm">
                                  {picksHidden ? (
                                    <span className="text-gray-400 text-xs">Hidden until first match</span>
                                  ) : allPicks.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {allPicks.map((p: any, i: number) => {
                                        const isOut =
                                          p?.advanced === 'NO' || p?.result === 'LOSS';
                                        return p?.isoCode ? (
                                          <span
                                            key={`${p.teamId}-${i}`}
                                            title={`${p.teamName} (Seed ${p.seed})${
                                              isOut ? ' — out' : ''
                                            }`}
                                            className={isOut ? 'opacity-40' : ''}
                                          >
                                            <CountryFlag
                                              iso={p.isoCode}
                                              alt={p.teamName}
                                              size="sm"
                                              rounded
                                            />
                                          </span>
                                        ) : null;
                                      })}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs">No picks</span>
                                  )}
                                </td>
                              </>
                            );
                          })() : pool.type === 'WORLD_CUP_PICK6' ? (() => {
                            const entryPicks = (entry as any).picks;
                            const teamPicks: any[] = Array.isArray(entryPicks?.teams)
                              ? entryPicks.teams
                              : [];
                            const eliminated: string[] = entryPicks?.eliminatedTeams || [];
                            const picksHidden =
                              pool.status === 'OPEN' && !isMyEntry && currentUser?.role !== 'ADMIN';
                            return (
                              <>
                                <td className="px-4 py-4 text-sm">
                                  {picksHidden ? (
                                    <span className="text-gray-400 text-xs">Hidden until first match</span>
                                  ) : teamPicks.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {teamPicks.map((p: any, i: number) => {
                                        const isOut = eliminated.includes(p.teamId);
                                        return p?.isoCode ? (
                                          <span
                                            key={`${p.teamId}-${i}`}
                                            title={`${p.teamName} (Seed ${p.seed})${
                                              isOut ? ' — eliminated' : ''
                                            }`}
                                            className={isOut ? 'opacity-40' : ''}
                                          >
                                            <CountryFlag
                                              iso={p.isoCode}
                                              alt={p.teamName}
                                              size="sm"
                                              rounded
                                            />
                                          </span>
                                        ) : null;
                                      })}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs">No picks</span>
                                  )}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                                  {Number(entry.score).toFixed(2)}
                                </td>
                              </>
                            );
                          })() : (
                            <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                              {Number(entry.score).toFixed(2)}
                            </td>
                          )}
                          <td className="px-4 py-4 whitespace-nowrap text-center text-sm">
                            <div className="flex items-center justify-center gap-2">
                              {/* Show View Picks button only if: pool is not OPEN, OR it's user's own entry, OR user is admin */}
                              {pool.status !== 'OPEN' || isMyEntry || currentUser?.role === 'ADMIN' ? (
                                <button
                                  onClick={() => {
                                    // Always pass the entry - LineupModal will fetch full details via API
                                    setSelectedEntry(entry);
                                  }}
                                  className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm hover:shadow-md"
                                >
                                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  View Picks
                                </button>
                              ) : (
                                <span className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-400 bg-gray-100 rounded-lg cursor-not-allowed" title="Picks will be visible after the deadline">
                                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                  Hidden
                                </span>
                              )}

                              {currentUser?.role === 'ADMIN' && (
                                <button
                                  onClick={() => setEditingEntry(entry)}
                                  className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm hover:shadow-md"
                                  title="Admin: Edit this entry's picks"
                                >
                                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  Edit Picks
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

        {/* Player Statistics (or Team Selection Statistics for World Cup pools)
            World Cup pools don't have player-level scoring — picks are by nation —
            so the "Player Statistics" panel is replaced by a count of how many
            entries picked each team. */}
        <div className="mt-6">
          {(() => {
            const isWorldCupPool =
              pool.type === 'WORLD_CUP_PICK6' || pool.type === 'WORLD_CUP_SURVIVOR';
            const sectionTitle = isWorldCupPool
              ? 'Team Selection Statistics'
              : 'Player Statistics';

            if (pool.status === 'OPEN') {
              return (
                <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">{sectionTitle}</h2>
                  <div className="text-center py-8">
                    <div className="mb-4">
                      <svg className="w-16 h-16 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{sectionTitle} Locked</h3>
                    <p className="text-gray-600 max-w-md mx-auto">
                      {isWorldCupPool
                        ? 'Selection information will be revealed after the tournament begins to ensure fair competition.'
                        : 'Player statistics and selection information will be revealed after kickoff of the first game to ensure fair competition.'}
                    </p>
                    {pool.deadline && (
                      <p className="text-sm text-blue-600 mt-3 font-medium">
                        Stats will be available after {new Date(pool.deadline).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              );
            }

            if (isWorldCupPool) {
              // Aggregate team picks across all entries.
              // Pick 6: picks.teams[] (6 nations per entry).
              // Survivor: picks.groupStageTeams[] + picks.knockoutPicks[round].
              type TeamPick = { teamId: string; teamName: string; seed?: number; isoCode?: string };
              const teamPickMap = new Map<string, { team: TeamPick; count: number }>();
              const totalEntries = entries.length;

              for (const entry of entries) {
                const picks = (entry as any).picks;
                if (!picks) continue;
                const collected: TeamPick[] = [];
                if (picks.type === 'WORLD_CUP_BRACKET_PREDICTION' && Array.isArray(picks.teams)) {
                  collected.push(...picks.teams);
                } else if (picks.type === 'WORLD_CUP_SURVIVOR') {
                  if (Array.isArray(picks.groupStageTeams)) collected.push(...picks.groupStageTeams);
                  if (picks.knockoutPicks && typeof picks.knockoutPicks === 'object') {
                    for (const v of Object.values(picks.knockoutPicks)) {
                      if (v && typeof v === 'object') collected.push(v as TeamPick);
                    }
                  }
                }
                // De-dupe within an entry — counting "% of entries that picked X" not "total selection events"
                const seen = new Set<string>();
                for (const t of collected) {
                  if (!t?.teamId || seen.has(t.teamId)) continue;
                  seen.add(t.teamId);
                  const existing = teamPickMap.get(t.teamId);
                  if (existing) {
                    existing.count++;
                  } else {
                    teamPickMap.set(t.teamId, { team: t, count: 1 });
                  }
                }
              }

              const ranked = Array.from(teamPickMap.values()).sort((a, b) => {
                if (b.count !== a.count) return b.count - a.count;
                return (a.team.seed ?? 99) - (b.team.seed ?? 99);
              });

              return (
                <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900">{sectionTitle}</h2>
                    <span className="text-sm text-gray-500">
                      {totalEntries} {totalEntries === 1 ? 'entry' : 'entries'} · {ranked.length} unique nation{ranked.length !== 1 ? 's' : ''} picked
                    </span>
                  </div>
                  {ranked.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-6">No selection data yet.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {ranked.map(({ team, count }) => {
                        const pct = totalEntries > 0 ? Math.round((count / totalEntries) * 100) : 0;
                        return (
                          <div
                            key={team.teamId}
                            className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded"
                          >
                            {team.isoCode && (
                              <CountryFlag iso={team.isoCode} alt={team.teamName} size="sm" rounded />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {team.teamName}
                                {team.seed && (
                                  <span className="text-xs text-gray-500 ml-1">· Seed {team.seed}</span>
                                )}
                              </p>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-1.5 bg-gray-200 rounded overflow-hidden">
                                  <div
                                    className="h-full bg-emerald-500"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-600 whitespace-nowrap">
                                  {count} ({pct}%)
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">{sectionTitle}</h2>
                <PlayerStats playerStats={playerStats} loading={statsLoading} />
              </div>
            );
          })()}
        </div>

        {/* ENHANCED Rules Section */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mt-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">📋 Complete Pool Rules & Guide</h2>
          
          <div className="space-y-6">
            {/* Overview */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
              <h3 className="font-bold text-gray-900 mb-2">🎯 How This Pool Works</h3>
              <p className="text-gray-700">{rulesContent.overview}</p>
            </div>

            {/* Roster Format */}
            {rulesContent.rosterFormat && (
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3">
                  {pool.type === 'NFL_NO_REPEAT' ? '👥 Weekly Lineup Requirements' : '👥 Roster Requirements'}
                </h3>
                <p className="text-gray-700 mb-2">
                  {pool.type === 'NFL_NO_REPEAT'
                    ? <>Each week, submit <strong>{rulesContent.rosterFormat.total} starters:</strong></>
                    : <>Select <strong>{rulesContent.rosterFormat.total} players total:</strong></>}
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4 mb-3">
                  {rulesContent.rosterFormat.positions.map((pos: string, idx: number) => (
                    <li key={idx}>{pos}</li>
                  ))}
                </ul>
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">⚠️ Critical Restriction</h4>
                      <p className="text-gray-700 text-sm font-medium">{rulesContent.rosterFormat.constraint}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* F1 Driver Usage Requirements */}
            {pool.type === 'FORMULA_1' && (
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3">🏎️ Driver Selection & Usage Limits</h3>
                <p className="text-gray-700 mb-3">For each race weekend, select exactly 2 drivers:</p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">Regular Season (Races 1-20)</h5>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        <li className="flex items-center">
                          <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold mr-2 text-xs">2</span>
                          <span>Drivers per race</span>
                        </li>
                        <li className="flex items-center">
                          <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold mr-2 text-xs">2</span>
                          <span>Max uses per driver (entire regular season)</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-2">Playoffs (Races 21-24)</h5>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        <li className="flex items-center">
                          <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold mr-2 text-xs">2</span>
                          <span>Drivers per race</span>
                        </li>
                        <li className="flex items-center">
                          <span className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center font-bold mr-2 text-xs">1</span>
                          <span>Max uses per driver (playoffs only)</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 mt-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">⚠️ Critical: Driver Locking</h4>
                      <p className="text-gray-700 text-sm">
                        Once you've used a driver the maximum number of times, they become <strong className="text-red-600">LOCKED</strong> until the next phase.
                        Example: Use Verstappen in Race 1 and Race 5 → He's locked until Playoffs!
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-3">
                  💡 There are 20 drivers in F1. With 2 uses each across 20 races, you'll use every driver exactly twice during the regular season!
                </p>
              </div>
            )}

            {/* Knockout Pick Requirements (NCAA Basketball Knockout only) */}
            {rulesContent.knockoutPickRequirements && (
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3">🏀 Daily Pick Requirements</h3>
                <p className="text-gray-700 mb-3">Here's how many winners you need to pick each day:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-orange-50 border-b-2 border-orange-200">
                        <th className="px-4 py-2 text-left font-semibold text-gray-900">Day</th>
                        <th className="px-4 py-2 text-left font-semibold text-gray-900">Round</th>
                        <th className="px-4 py-2 text-center font-semibold text-gray-900">Picks</th>
                        <th className="px-4 py-2 text-left font-semibold text-gray-900">Buy-Back Option</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rulesContent.knockoutPickRequirements.map((req: any, idx: number) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-4 py-2 font-medium text-gray-900">{req.day}</td>
                          <td className="px-4 py-2 text-gray-700">{req.round}</td>
                          <td className="px-4 py-2 text-center">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full font-bold">
                              {req.picks}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-gray-600 text-xs">{req.buyBack}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Tournament Format */}
            {rulesContent.tournamentFormat && (
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3">🏆 Tournament Format & Scoring Timeline</h3>
                <p className="text-gray-700 mb-3">
                  {pool.type === 'NCAA_BASKETBALL_KNOCKOUT'
                    ? 'Survive each round by picking winners:'
                    : pool.type === 'NCAA_BASKETBALL_PICK6'
                    ? 'Your teams earn points based on seed and round advancement:'
                    : pool.type === 'WORLD_CUP_PICK6'
                    ? 'Your nations earn points each round they advance — higher FIFA seeds = bigger multipliers:'
                    : pool.type === 'WORLD_CUP_SURVIVOR'
                    ? 'Survive each round by picking winners (one-and-done across both phases):'
                    : pool.type === 'NFL_NO_REPEAT'
                    ? 'A fresh 7-man lineup every week — each player usable only once all season:'
                    : 'Your players accumulate points throughout the tournament as follows:'}
                </p>
                <div className="space-y-2">
                  {rulesContent.tournamentFormat.map((stage: any, idx: number) => (
                    <div key={idx} className="flex items-start bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">
                        {idx + 1}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{stage.phase}</h4>
                        <p className="text-sm text-gray-600">{stage.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Key Rules */}
            {rulesContent.keyRules && rulesContent.keyRules.length > 0 && (
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3">📌 Important Rules to Remember</h3>
                <ul className="space-y-2">
                  {rulesContent.keyRules.map((rule: string, idx: number) => (
                    <li key={idx} className="flex items-start">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Scoring - Dynamic by pool type */}
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">💯 Scoring System</h3>
              {pool.type === 'NCAA_BASKETBALL_KNOCKOUT' ? (
                <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border-2 border-red-200">
                  <h4 className="font-bold text-gray-900 mb-3">🏀 Survivor Format - No Points!</h4>
                  <div className="space-y-2 text-gray-700">
                    <p><strong>WIN:</strong> Your pick wins → You survive to the next day ✅</p>
                    <p><strong>LOSS:</strong> Any pick loses → You are <span className="text-red-600 font-bold">ELIMINATED</span> ❌</p>
                    <p><strong>BUY-BACK:</strong> $10 to get back in (max 3, before Sunday only)</p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-red-200">
                    <p className="text-sm text-gray-600">
                      <strong>Winner:</strong> Last person standing wins the pot! If multiple survivors remain after the Championship, they split the prize.
                    </p>
                  </div>
                </div>
              ) : pool.type === 'NCAA_BASKETBALL_PICK6' ? (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-3">📊 Points = Seed × Round Multiplier</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">Round 1</p>
                      <p className="font-bold text-lg">Seed × 1</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">Round 2</p>
                      <p className="font-bold text-lg">Seed × 3</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">Sweet 16</p>
                      <p className="font-bold text-lg">Seed × 5</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">Elite 8</p>
                      <p className="font-bold text-lg">Seed × 7</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">Final Four</p>
                      <p className="font-bold text-lg">Seed × 9</p>
                    </div>
                    <div className="text-center p-2 bg-yellow-50 rounded border-2 border-yellow-400">
                      <p className="text-xs text-gray-500">Championship</p>
                      <p className="font-bold text-lg text-yellow-700">Seed × 11</p>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-3">
                    💡 Example: A 12-seed reaching the Elite 8 = 12×1 + 12×3 + 12×5 + 12×7 = 192 points!
                  </p>
                </div>
              ) : pool.type === 'FORMULA_1' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">🏁 Grand Prix Race Points</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>1st Place: <strong className="text-yellow-600">25 pts</strong></li>
                      <li>2nd Place: <strong>18 pts</strong></li>
                      <li>3rd Place: <strong className="text-orange-600">15 pts</strong></li>
                      <li>4th Place: <strong>12 pts</strong></li>
                      <li>5th Place: <strong>10 pts</strong></li>
                      <li>6th Place: <strong>8 pts</strong></li>
                      <li>7th Place: <strong>6 pts</strong></li>
                      <li>8th Place: <strong>4 pts</strong></li>
                      <li>9th Place: <strong>2 pts</strong></li>
                      <li>10th Place: <strong>1 pt</strong></li>
                      <li>11th+ / DNF: <strong className="text-red-600">0 pts</strong></li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">⚡ Sprint Race Points</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>1st Place: <strong className="text-yellow-600">8 pts</strong></li>
                      <li>2nd Place: <strong>7 pts</strong></li>
                      <li>3rd Place: <strong className="text-orange-600">6 pts</strong></li>
                      <li>4th Place: <strong>5 pts</strong></li>
                      <li>5th Place: <strong>4 pts</strong></li>
                      <li>6th Place: <strong>3 pts</strong></li>
                      <li>7th Place: <strong>2 pts</strong></li>
                      <li>8th Place: <strong>1 pt</strong></li>
                      <li>9th+ / DNF: <strong className="text-red-600">0 pts</strong></li>
                    </ul>
                    <p className="text-xs text-blue-600 mt-2">💡 6 sprint weekends in 2026 for bonus scoring!</p>
                  </div>
                </div>
              ) : (pool.type === 'OLYMPIC_HOCKEY' || pool.type === 'FOUR_NATIONS' || pool.type === 'NHL_PLAYOFF') ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Skaters (Forwards & Defensemen)</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>Goal (G): <strong>6 pts</strong></li>
                      <li>Assist (A): <strong>4 pts</strong></li>
                      <li>Plus/Minus (+/-): <strong>2 pts</strong></li>
                      <li>Power-Play Point (PPP): <strong>2 pts</strong></li>
                      <li>Shot on Goal (SOG): <strong>0.9 pts</strong></li>
                      <li>Blocked Shot (BLK): <strong>1 pt</strong></li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Goaltenders</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>Win (W): <strong>5 pts</strong></li>
                      <li>Goals Against (GA): <strong>-3 pts each</strong></li>
                      <li>Save (SV): <strong>0.6 pts</strong></li>
                      <li>Shutout (SHO): <strong>5 pts</strong></li>
                    </ul>
                    <p className="text-xs text-blue-600 mt-2">
                      {pool.type === 'NHL_PLAYOFF'
                        ? '💡 Stanley Cup Final games count as DOUBLE points!'
                        : '💡 Example: Win + Shutout + 30 saves = 5 + 5 + 18 = 28 pts!'}
                    </p>
                  </div>
                </div>
              ) : pool.type === 'NBA_PLAYOFF' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Player Scoring</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>Points (PTS): <strong>1 pt</strong></li>
                      <li>Rebounds (REB): <strong>1 pt</strong></li>
                      <li>Assists (AST): <strong>1.5 pts</strong></li>
                      <li>Steals (STL): <strong>3 pts</strong></li>
                      <li>Blocks (BLK): <strong>3 pts</strong></li>
                      <li>Turnovers (TO): <strong className="text-red-600">-1 pt</strong></li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Bonus</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>NBA Finals games count as <strong className="text-yellow-600">DOUBLE points!</strong></li>
                    </ul>
                    <p className="text-xs text-blue-600 mt-2">💡 Example: A player with 30 PTS, 10 REB, 8 AST, 2 STL, 1 BLK, 3 TO = 30 + 10 + 12 + 6 + 3 - 3 = 58 pts!</p>
                  </div>
                </div>
              ) : pool.type === 'WORLD_CUP_PICK6' ? (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-3">⚽ Points = Seed × Round Factor</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">Group Stage</p>
                      <p className="font-bold text-lg">Seed × 4</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">Round of 32</p>
                      <p className="font-bold text-lg">Seed × 5</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">Round of 16</p>
                      <p className="font-bold text-lg">Seed × 7</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">Quarterfinals</p>
                      <p className="font-bold text-lg">Seed × 9</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded border">
                      <p className="text-xs text-gray-500">Semifinals</p>
                      <p className="font-bold text-lg">Seed × 11</p>
                    </div>
                    <div className="text-center p-2 bg-emerald-50 rounded border-2 border-emerald-400">
                      <p className="text-xs text-gray-500">Final</p>
                      <p className="font-bold text-lg text-emerald-700">Seed × 13</p>
                    </div>
                  </div>
                  <p className="text-xs text-emerald-700 mt-3">
                    💡 Example: A Seed 12 nation winning the World Cup = 48 + 60 + 84 + 108 + 132 + 156 = <strong>588 points</strong>. A Seed 1 winning it all = only 49.
                  </p>
                </div>
              ) : pool.type === 'WORLD_CUP_SURVIVOR' ? (
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border-2 border-emerald-200">
                  <h4 className="font-bold text-gray-900 mb-3">⚽ Survivor Format — No Points!</h4>
                  <div className="space-y-2 text-gray-700">
                    <p><strong>Phase 1 (Group Stage):</strong> Pick 6 nations. ALL 6 must advance to R32 → you survive ✅</p>
                    <p><strong>Phase 2 (Knockout):</strong> Pick 1 team per round to win outright (extra time / penalties count) ✅</p>
                    <p><strong>LOSS:</strong> Any pick fails to advance / loses → you are <span className="text-rose-600 font-bold">ELIMINATED</span> ❌</p>
                    <p><strong>One-and-done:</strong> A team used in Phase 1 OR an earlier knockout round can never be picked again.</p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-emerald-200">
                    <p className="text-sm text-gray-600">
                      <strong>Winner:</strong> Last entry standing wins the pot! If multiple survive after the Final, they may split or play on by mutual agreement.
                    </p>
                  </div>
                </div>
              ) : pool.type === 'NFL_NO_REPEAT' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Offensive Scoring (Full PPR)</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>Passing TD: 4 pts</li>
                      <li>Rushing/Receiving TD: 6 pts</li>
                      <li>20 passing yards: 1 pt</li>
                      <li>10 rushing/receiving yards: 1 pt</li>
                      <li>Reception (RB/WR/QB): 1 pt</li>
                      <li>Reception (TE): 1.5 pts</li>
                      <li>Interception: -1 pt</li>
                      <li>Fumble lost: -1 pt</li>
                      <li>2-pt conversion: 2 pts</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Offense Only — Every Week Equal</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>No kickers, no defenses/special teams</li>
                      <li>No double-point weeks — all 18 weeks count the same</li>
                      <li>Weekly score = the sum of your 7 starters' points</li>
                      <li>Season total = the sum of all your weekly scores</li>
                      <li>Empty slot or player on bye = 0 pts</li>
                    </ul>
                    <p className="text-xs text-blue-600 mt-2">💡 Example: 300-yd, 3-TD QB game with 1 INT = 15 + 12 − 1 = 26 pts</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Offensive Scoring</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>Passing TD: 4 pts</li>
                      <li>Rushing/Receiving TD: 6 pts</li>
                      <li>20 passing yards: 1 pt</li>
                      <li>10 rushing/receiving yards: 1 pt</li>
                      <li>Reception (RB/WR/QB): 1 pt</li>
                      <li>Reception (TE): 1.5 pts</li>
                      <li>Interception: -1 pt</li>
                      <li>Fumble lost: -1 pt</li>
                      <li>2-pt conversion: 2 pts</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Kicking</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>FG (1-30 yards): 3 pts</li>
                      <li>FG (31+ yards): 3 + 0.1/yard</li>
                      <li>PAT: 1 pt</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Defense/Special Teams</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>Sack: 1 pt</li>
                      <li>Turnover: 2 pts</li>
                      <li>TD: 6 pts</li>
                      <li>Safety: 5 pts</li>
                      <li>Shutout: 12 pts</li>
                      <li>Allow 1-6 pts: 8 pts</li>
                      <li>Allow 7-10 pts: 5 pts</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* F1 Playoff Advancement */}
            {pool.type === 'FORMULA_1' && (
              <div className="bg-gradient-to-r from-purple-50 to-red-50 border-2 border-red-400 rounded-lg p-4">
                <div className="flex items-start">
                  <span className="text-4xl mr-3">🏆</span>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 mb-1">Playoff Advancement (After Race 20)</h3>
                    <p className="text-gray-700 mb-3">
                      After the regular season, the <strong className="text-purple-600">top 40%</strong> of entries advance to the playoffs for the final 4 races!
                    </p>
                    <div className="bg-white rounded p-3 border border-red-300 space-y-2">
                      <p className="text-sm font-medium text-gray-900">Playoff Benefits:</p>
                      <ul className="text-sm text-gray-700 ml-4 space-y-1">
                        <li>✅ <strong>Driver usage RESETS</strong> – all drivers become available again</li>
                        <li>✅ Each driver can be used <strong>1 time</strong> during playoffs</li>
                        <li>✅ Continue earning points toward your total score</li>
                      </ul>
                      <p className="text-sm font-medium text-red-700 mt-3">Eliminated Entries:</p>
                      <p className="text-sm text-gray-700 ml-4">
                        Entries outside top 40% are eliminated and cannot make picks for races 21-24.
                      </p>
                    </div>
                    <p className="text-sm text-gray-700 mt-3">
                      ⚡ <strong>Example:</strong> With 50 entries, top 20 (40%) advance to playoffs.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Championship Bonus - Not shown for NCAA Basketball, F1, World Cup, or NFL No-Repeat pools (none use double-point Championship bonus) */}
            {pool.type !== 'NCAA_BASKETBALL_KNOCKOUT' && pool.type !== 'NCAA_BASKETBALL_PICK6' && pool.type !== 'FORMULA_1' && pool.type !== 'WORLD_CUP_PICK6' && pool.type !== 'WORLD_CUP_SURVIVOR' && pool.type !== 'NFL_NO_REPEAT' && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 mb-1">🏆 {pool.type === 'NHL_PLAYOFF' ? 'Stanley Cup Final' : pool.type === 'NBA_PLAYOFF' ? 'NBA Finals' : pool.type === 'OLYMPIC_HOCKEY' ? 'Gold Medal Game' : pool.type === 'CFP' ? 'National Championship' : pool.type === 'NFL_PLAYOFF' ? 'Super Bowl' : 'Championship'} DOUBLE POINTS!</h3>
                    <p className="text-gray-700 mb-2">
                      All points earned in the {pool.type === 'NHL_PLAYOFF' ? 'Stanley Cup Final' : pool.type === 'NBA_PLAYOFF' ? 'NBA Finals' : pool.type === 'OLYMPIC_HOCKEY' ? 'Gold Medal Game' : pool.type === 'CFP' ? 'National Championship Game' : pool.type === 'NFL_PLAYOFF' ? 'Super Bowl' : 'Championship Game'} count as <strong className="text-2xl text-yellow-700">DOUBLE</strong>!
                    </p>
                    <p className="text-sm text-gray-600">
                      {pool.type === 'NBA_PLAYOFF'
                        ? '💡 Example: A player scores 30 points with 10 rebounds in the Finals → 40 pts normally → 80 pts doubled!'
                        : '💡 Example: A player scores 2 goals in the championship → 12 pts normally → 24 pts doubled!'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Strategy Tips */}
            {rulesContent.strategyTips && rulesContent.strategyTips.length > 0 && (
              <div>
                <h3 className="font-semibold text-lg text-gray-900 mb-3">💡 Winning Strategy Tips</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {rulesContent.strategyTips.map((item: any, idx: number) => (
                    <div key={idx} className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h4 className="font-medium text-gray-900 mb-1">✓ {item.tip}</h4>
                      <p className="text-sm text-gray-600">{item.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Prizes */}
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">🏅 Prize Distribution</h3>
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-300">
                {pool.type === 'NCAA_BASKETBALL_KNOCKOUT' ? (
                  <>
                    <div className="flex items-center text-gray-700">
                      <span className="text-2xl mr-3">🏆</span>
                      <span><strong>Winner Takes All:</strong> Last person standing wins 100% of the prize pool</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-3 pt-3 border-t border-gray-300">
                      If multiple survivors remain after the Championship, the pot is split evenly among them.
                    </p>
                  </>
                ) : (
                  <>
                    <ul className="text-gray-700 space-y-2">
                      <li className="flex items-center">
                        <span className="text-2xl mr-3">🥇</span>
                        <span><strong>1st Place:</strong> 60% of total entry fees</span>
                      </li>
                      <li className="flex items-center">
                        <span className="text-2xl mr-3">🥈</span>
                        <span><strong>2nd Place:</strong> 30% of total entry fees</span>
                      </li>
                      <li className="flex items-center">
                        <span className="text-2xl mr-3">🥉</span>
                        <span><strong>3rd Place:</strong> 10% of total entry fees</span>
                      </li>
                    </ul>
                    <p className="text-sm text-gray-600 mt-3 pt-3 border-t border-gray-300">
                      <strong>Tie-Breaker:</strong> If multiple entries tie for a position, the prize amounts are combined and split evenly.
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Deadline & Entry Locking */}
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">
                    {pool.type === 'NFL_NO_REPEAT' ? '⏰ Deadlines & Weekly Locking' : '⏰ Entry Deadline & Roster Locking'}
                  </h3>
                  {pool.type === 'NFL_NO_REPEAT' ? (
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>• <strong>Join Deadline:</strong> {new Date(pool.deadline).toLocaleString()} — entries stay open into Week 1, right up to Sunday's early games</li>
                      <li>• <strong>Joining late?</strong> Your Week 1 lineup can only use players whose games haven't started yet</li>
                      <li>• <strong>Weekly Lineups:</strong> Set or change each week's lineup any time before that player's game starts</li>
                      <li>• <strong>Per-Player Locks:</strong> Each slot locks at its player's own kickoff — later games stay editable</li>
                      <li>• <strong>Pick Reveal:</strong> Each pick goes public at that player's kickoff — unplayed picks stay hidden</li>
                      <li>• <strong>Tip:</strong> Pre-fill future weeks early — you can adjust any unlocked slot right up to kickoff!</li>
                    </ul>
                  ) : (
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• <strong>Deadline:</strong> {new Date(pool.deadline).toLocaleString()}</li>
                    <li>• <strong>After Deadline:</strong> NO changes allowed - rosters are permanently locked</li>
                    <li>• <strong>Public Rosters:</strong> All entries become visible to other participants</li>
                    <li>• <strong>Tip:</strong> Submit early and double-check your picks before the deadline!</li>
                  </ul>
                  )}
                </div>
              </div>
            </div>

            {/* Leaderboard Info */}
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">📊 Leaderboard & Standings</h3>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• Scores update after each game's official statistics are posted</li>
                  <li>• Rankings automatically sort by total cumulative points</li>
                  <li>• View any participant's roster by clicking "View Lineup"</li>
                  <li>• Check back daily during the tournament to track your position</li>
                  <li>• Your entry is highlighted in blue on the leaderboard</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
